import DashboardLayout from "@/components/DashboardLayout";
import {
  formatCustomerAddressLine,
} from "@/components/ServiceTicketCustomerHeader";
import { CustomerNoticePanel } from "@/components/CustomerNoticePanel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AddressSearchInput } from "@/components/AddressSearchInput";
import {
  toastSvrNotificationWarnings,
} from "@/components/site-visits/SvrInventoryTeamFields";
import { SvrAdditionalItemsCard } from "@/components/site-visits/SvrAdditionalItemsCard";
import {
  clockInputsFromArrivalWindow,
  SvrVisitScheduleAlert,
} from "@/components/site-visits/SvrVisitScheduleDetails";
import { useAuth } from "@/_core/hooks/useAuth";
import { formatSvrBillingDecisionLabel } from "@shared/svrBillingDecision";
import {
  buildSvrInvoiceWhatsAppConfirmation,
  evaluateSvrInvoiceWhatsAppEligibility,
  renderSvrInvoiceReminderWhatsAppMessage,
  staffSvrWhatsAppLabel,
} from "@shared/svrInvoiceWhatsAppEligibility";
import { buildSvrInvoiceContentFingerprint } from "@shared/svrInvoiceContentFingerprint";
import { resolveSvrInvoicePdfPath } from "@shared/svrInvoicePdfPath";
import { formatSvrBillingAmountDisplay, formatSvrBillingMoneySgd } from "@shared/svrBillingMoney";
import { canRecordSvrManualPayment } from "@shared/svrManualPaymentEligibility";
import { trpc } from "@/lib/trpc";
import { fullCustomerSearchHits } from "@shared/customerSearchAccess";
import { mapCustomerUpdateErrorMessage } from "@/lib/customerFormErrors";
import { toast } from "sonner";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ClipboardList,
  Copy,
  DollarSign,
  Download,
  FileText,
  Flag,
  Info,
  Loader2,
  MapPin,
  MessageCircle,
  MessageSquare,
  MoreVertical,
  Pencil,
  Play,
  RefreshCw,
  Sparkles,
  Upload,
  User,
  XCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEffect, useRef, useState } from "react";
import { MarkPaidReceiptModeDialog } from "@/components/finance/MarkPaidReceiptModeDialog";
import { logFieldCalendarUrlCrash } from "@/components/field-calendar/fieldCalendarDiagnostics";
import { useLocation, useParams } from "wouter";
import { useHistoryState } from "wouter/use-browser-location";
import { formatDate, formatDateTime } from "@/lib/dateUtils";
import { formatSingaporeDate, parseSingaporeDateInput, toSingaporeDateInputValue } from "@shared/timezone";
import { parseValidArrivalWindow } from "@shared/svrSchedulingIntegrity";
import { buildSvrTrackingUrl } from "@/lib/publicUrl";
import { devLog } from "@/lib/debugLog";
import { resolveSvrBackTarget } from "@/components/site-visits/svrBackNavigation";
import {
  SVR_FOCUSED_STAGES,
  buildSvrActivityEvents,
  buildSvrFocusedWorkflow,
  canViewSvrFinancialDetails,
} from "@shared/svrFocusedWorkflow";

const WORKFLOW_STAGES = [
  { id: "service", label: "Service", statuses: ["scheduled"] },
  { id: "pic", label: "PIC", statuses: ["pending_review"] },
  { id: "finance", label: "Finance", statuses: ["report_sent", "awaiting_payment"] },
  { id: "close", label: "Close", statuses: ["closed", "waived"] },
] as const;

type SvrExpandedStageId = (typeof WORKFLOW_STAGES)[number]["id"];

function normalizeSvrStatusKey(status: string | null | undefined): string {
  return String(status ?? "").toLowerCase();
}

function statusToStageId(status: string): SvrExpandedStageId {
  const key = normalizeSvrStatusKey(status);
  return (
    WORKFLOW_STAGES.find((stage) => (stage.statuses as readonly string[]).includes(key))?.id ?? "service"
  ) as SvrExpandedStageId;
}

function parseBillingAuditLog(raw: unknown): Array<{ type: string; userName: string; amount?: number; at: string }> {
  if (!raw || typeof raw !== "string") return [];
  try {
    const a = JSON.parse(raw);
    return Array.isArray(a) ? a : [];
  } catch {
    return [];
  }
}

// Formats an arrivalWindow slot string (e.g. "9:00-9:30" or legacy "9am-10am") to a human-readable label
function formatArrivalWindow(slot: string): string {
  if (!slot) return "";
  const legacyMap: Record<string, string> = {
    "9am-10am": "9:00am – 10:00am",
    "10am-11am": "10:00am – 11:00am",
    "11am-12pm": "11:00am – 12:00pm",
    "2pm-3pm": "2:00pm – 3:00pm",
    "3pm-4pm": "3:00pm – 4:00pm",
    "4pm-5pm": "4:00pm – 5:00pm",
  };
  if (legacyMap[slot]) return legacyMap[slot];

  // Handle slots that already carry am/pm suffix, e.g. "10:00am-10:30am" or "10:00am-10:30pm"
  // Split on the dash that separates start from end (not the colon inside each time part)
  const dashIdx = slot.indexOf("-", slot.indexOf(":") + 1); // skip colon inside time
  const s = dashIdx >= 0 ? slot.slice(0, dashIdx) : slot;
  const e = dashIdx >= 0 ? slot.slice(dashIdx + 1) : "";

  const fmt = (t: string) => {
    // Strip any am/pm suffix before parsing, track it
    const suffix = t.endsWith("am") ? "am" : t.endsWith("pm") ? "pm" : null;
    const clean = suffix ? t.slice(0, -2) : t;
    const [hStr, mStr] = clean.split(":");
    let h = Number(hStr);
    const m = Number(mStr ?? 0);
    if (isNaN(h) || isNaN(m)) return t; // fallback: return raw
    // If suffix provided, convert to 24h for am/pm determination
    if (suffix === "pm" && h !== 12) h += 12;
    if (suffix === "am" && h === 12) h = 0;
    const ampm = h < 12 ? "am" : "pm";
    const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${h12}:${String(m).padStart(2, "0")}${ampm}`;
  };

  return s && e ? `${fmt(s)} – ${fmt(e)}` : slot;
}

// Converts "H:MM-H:MM" slot to time input value "HH:MM"
function slotToTimeInput(slot: string, part: "start" | "end"): string {
  if (!slot) return "";
  const legacyStartMap: Record<string, string> = {
    "9am-10am": "09:00", "10am-11am": "10:00", "11am-12pm": "11:00",
    "2pm-3pm": "14:00", "3pm-4pm": "15:00", "4pm-5pm": "16:00",
  };
  const legacyEndMap: Record<string, string> = {
    "9am-10am": "10:00", "10am-11am": "11:00", "11am-12pm": "12:00",
    "2pm-3pm": "15:00", "3pm-4pm": "16:00", "4pm-5pm": "17:00",
  };
  if (part === "start" && legacyStartMap[slot]) return legacyStartMap[slot];
  if (part === "end" && legacyEndMap[slot]) return legacyEndMap[slot];
  const [s, e] = slot.split("-");
  const t = part === "start" ? s : e;
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  return `${String(h).padStart(2, "0")}:${String(m ?? 0).padStart(2, "0")}`;
}

// ── Free-form arrival window helpers ────────────────────────────────────────
// Converts a stored arrivalWindow value (any format) to a HH:MM time input value
function windowToTimeInput(window: string | null | undefined, part: "start" | "end"): string {
  if (!window) return part === "start" ? "09:00" : "17:00";
  // Already in HH:MM-HH:MM format
  if (/^\d{2}:\d{2}-\d{2}:\d{2}$/.test(window)) {
    const [s, e] = window.split("-");
    return part === "start" ? s : e;
  }
  // Legacy enum values
  const legacyMap: Record<string, [string, string]> = {
    "8am-8:30am": ["08:00","08:30"], "8:30am-9am": ["08:30","09:00"],
    "9am-9:30am": ["09:00","09:30"], "9:30am-10am": ["09:30","10:00"],
    "10am-10:30am": ["10:00","10:30"], "10:30am-11am": ["10:30","11:00"],
    "11am-11:30am": ["11:00","11:30"], "11:30am-12pm": ["11:30","12:00"],
    "12pm-12:30pm": ["12:00","12:30"], "12:30pm-1pm": ["12:30","13:00"],
    "1pm-1:30pm": ["13:00","13:30"], "1:30pm-2pm": ["13:30","14:00"],
    "2pm-2:30pm": ["14:00","14:30"], "2:30pm-3pm": ["14:30","15:00"],
    "3pm-3:30pm": ["15:00","15:30"], "3:30pm-4pm": ["15:30","16:00"],
    "4pm-4:30pm": ["16:00","16:30"], "4:30pm-5pm": ["16:30","17:00"],
    "5pm-5:30pm": ["17:00","17:30"], "5:30pm-6pm": ["17:30","18:00"],
    "6pm-6:30pm": ["18:00","18:30"], "6:30pm-7pm": ["18:30","19:00"],
    "7pm-7:30pm": ["19:00","19:30"], "7:30pm-8pm": ["19:30","20:00"],
    "9am-10am": ["09:00","10:00"], "10am-11am": ["10:00","11:00"],
    "11am-12pm": ["11:00","12:00"], "2pm-3pm": ["14:00","15:00"],
    "3pm-4pm": ["15:00","16:00"], "4pm-5pm": ["16:00","17:00"],
  };
  const mapped = legacyMap[window];
  if (mapped) return part === "start" ? mapped[0] : mapped[1];
  return part === "start" ? "09:00" : "17:00";
}

// ── SVR stage section: accordion card only (workflow chrome lives in the left spine) ──
function SvrStageSection({
  label,
  active,
  past,
  open,
  onRequestExpand,
  statusSubline,
  children,
}: {
  label: string;
  active: boolean;
  past: boolean;
  /** Controlled: exactly one stage expanded at a time (parent state). */
  open: boolean;
  onRequestExpand: () => void;
  /** Shown in header when collapsed (e.g. "Completed on …" / "Upcoming") */
  statusSubline?: string;
  children: React.ReactNode;
}) {
  const isUpcoming = !active && !past;
  return (
    <div
      className={cn(
        "rounded-xl transition-colors",
        active && "border-2 border-violet-400/70 bg-white shadow-[0_18px_42px_-30px_rgba(76,29,149,0.55)]",
        !active && past && "border border-slate-200/90 bg-white/85",
        isUpcoming && "border border-slate-200/90 bg-slate-50/80",
        "mt-4"
      )}
    >
      <button
        type="button"
        onClick={() => onRequestExpand()}
        className={cn(
          "flex w-full items-center justify-between gap-3 rounded-t-xl px-4 text-left hover:bg-slate-50/80",
          active ? "py-3.5" : "py-2.5"
        )}
      >
        <div className="min-w-0 flex flex-1 items-center gap-2 flex-wrap">
          <span
            className={cn(
              "text-xs font-bold tracking-widest",
              active && "text-violet-800",
              !active && past && "text-slate-800",
              isUpcoming && "text-slate-500"
            )}
          >
            {label.toUpperCase()}
          </span>
          {past && !active && <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" strokeWidth={3} />}
          {isUpcoming && <span className="text-xs text-slate-400">Upcoming</span>}
          {past && !active && statusSubline && (
            <span className="text-[11px] text-slate-500">· {statusSubline}</span>
          )}
        </div>
        <ChevronDown
          className={cn("h-4 w-4 shrink-0 text-slate-400 transition-transform", open && "rotate-180")}
        />
      </button>
      {open && (
        <div className={cn("space-y-3 border-t border-slate-100 px-4 pb-4", active ? "pt-4" : "pt-3")}>
          {children}
        </div>
      )}
    </div>
  );
}

function CustomerWhatsAppButton({
  visitId,
  onSuccess,
  alreadySent,
}: {
  visitId: number;
  onSuccess: () => void;
  alreadySent?: boolean;
}) {
  const resend = trpc.siteVisits.resendCustomerWhatsApp.useMutation({
    onSuccess: () => {
      toast.success(
        alreadySent
          ? "WhatsApp notification resent to customer."
          : "WhatsApp notification sent to customer."
      );
      onSuccess();
    },
    onError: (e) => toast.error(e.message),
  });
  return (
    <button
      onClick={(ev) => { ev.stopPropagation(); resend.mutate({ id: visitId }); }}
      disabled={resend.isPending}
      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
      title={alreadySent ? "Resend WhatsApp notification to customer" : "Send WhatsApp notification to customer"}
    >
      {resend.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
      {alreadySent ? "Resend WhatsApp notification" : "Send WhatsApp notification to customer"}
    </button>
  );
}

export default function SiteVisitDetail() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const historyState = useHistoryState<
    | {
        from?: string;
        taskOpen?: { type: string; id: number; referenceNumber?: string; noticeSourceType?: string };
      }
    | undefined
  >();
  const { user } = useAuth();
  const routeId = (params.id ?? "").trim();
  const visitIdFromRoute = Number.parseInt(routeId, 10);
  const hasValidRouteId = routeId.length > 0;
  const routeLookupId: number | string =
    Number.isFinite(visitIdFromRoute) && visitIdFromRoute > 0 ? visitIdFromRoute : routeId;
  const isAdmin = user?.role === "admin";

  // Detect ?costing=1 param — set by MyTasks when a billable SVR needs costing added
  const needsCostingBanner = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("costing") === "1";
  const financeSectionRef = useRef<HTMLDivElement>(null);

  const [expandedStageId, setExpandedStageId] = useState<SvrExpandedStageId>("service");

  const readBackContext = () => {
    let returnToRaw: string | null = null;
    let sessionReferrer: string | null = null;
    if (typeof window !== "undefined") {
      try {
        returnToRaw = new URLSearchParams(window.location.search).get("returnTo");
      } catch (error) {
        logFieldCalendarUrlCrash("goBack.searchParams", error, {});
      }
      try {
        sessionReferrer = sessionStorage.getItem("svrDetailReferrer");
      } catch (error) {
        logFieldCalendarUrlCrash("goBack.session", error, {});
      }
    }
    const historyFrom =
      historyState &&
      typeof historyState === "object" &&
      historyState !== null &&
      "from" in historyState &&
      typeof (historyState as { from?: unknown }).from === "string"
        ? (historyState as { from: string }).from
        : null;
    return resolveSvrBackTarget({
      returnTo: returnToRaw,
      historyFrom,
      sessionReferrer,
      historyLength: typeof window !== "undefined" ? window.history.length : 1,
    });
  };

  const goBack = () => {
    try {
      const target = readBackContext();
      if (target.clearSessionReferrer) {
        try {
          sessionStorage.removeItem("svrDetailReferrer");
        } catch (error) {
          logFieldCalendarUrlCrash("goBack.session", error, {});
        }
      }
      if (target.useHistory && typeof window !== "undefined") {
        window.history.back();
        return;
      }
      setLocation(target.path ?? "/site-visits");
    } catch (error) {
      logFieldCalendarUrlCrash("goBack", error, {});
      setLocation("/site-visits");
    }
  };

  const [focusedActionError, setFocusedActionError] = useState<string | null>(null);
  // Scope of Work inline edit state
  const [editingScopeOfWork, setEditingScopeOfWork] = useState(false);
  const [scopeOfWorkText, setScopeOfWorkText] = useState("");
  // State for technician update form
  const [internalNotes, setInternalNotes] = useState("");
  /** When true, local internal note edits take priority; when false, server (e.g. Cleo appends) can refresh the textarea. */
  const [internalNotesDirty, setInternalNotesDirty] = useState(false);
  const [findingsAndActions, setFindingsAndActions] = useState("");
  const [customerReport, setCustomerReport] = useState("");
  const [partsCost, setPartsCost] = useState("");
  const [waiverReason, setWaiverReason] = useState("");
  const [showWaiverForm, setShowWaiverForm] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");

  // State for customer editing
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [editName, setEditName] = useState("");
  const [editContact, setEditContact] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editUnitNumber, setEditUnitNumber] = useState("");
  const [editPostalCode, setEditPostalCode] = useState("");
  const [editSalespersonId, setEditSalespersonId] = useState<number | null>(null);

  // Direct SVR PIC assignment state (admin only)
  const [editingPic, setEditingPic] = useState(false);
  const [selectedPicId, setSelectedPicId] = useState<number | "">("");

  // Finance billing state
  const [selectedRate, setSelectedRate] = useState<number | null>(null);
  const [billingNotes, setBillingNotes] = useState("");
  // Billing decision dialog state (for PIC close/send-report action)
  const [showBillingDialog, setShowBillingDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<"close" | "send_report" | null>(null);
  const [billingDecision, setBillingDecision] = useState<
    | "bill"
    | "no_charge_warranty_goodwill"
    | "no_charge_contract"
    | "not_billable_new_job_within_1_year"
    | ""
  >("");
  const [agreedRate, setAgreedRate] = useState<string>("");
  const [billingNote, setBillingNote] = useState<string>("");
  const [showMatchCustomerModal, setShowMatchCustomerModal] = useState(false);
  const [matchTab, setMatchTab] = useState<"existing" | "new">("existing");
  const [matchQuery, setMatchQuery] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [newCustomerName, setNewCustomerName] = useState("");
  const [newCustomerPhone, setNewCustomerPhone] = useState("");
  const [newCustomerEmail, setNewCustomerEmail] = useState("");
  const [newCustomerAddress, setNewCustomerAddress] = useState("");
  const [newCustomerPostalCode, setNewCustomerPostalCode] = useState("");
  const [newCustomerUnitNumber, setNewCustomerUnitNumber] = useState("");
  const [newCustomerIsCondo, setNewCustomerIsCondo] = useState(false);
  const [newCustomerAddressQuery, setNewCustomerAddressQuery] = useState("");

  // Invoice upload ref
  const invoiceInputRef = useRef<HTMLInputElement>(null);

  // Multi-tech assignees state
  const [showAssigneePanel, setShowAssigneePanel] = useState(false);
  const [pendingAssignees, setPendingAssignees] = useState<Array<{ userId: number; userName: string; role: "technician" | "programmer" | "installer" }>>([]);

  const utils = trpc.useUtils();
  const { data: visit, isLoading, error, refetch: refetchVisit } = trpc.siteVisits.getById.useQuery(
    { id: routeLookupId },
    {
      enabled: hasValidRouteId,
      retry: 1,
      retryDelay: 1000,
      // Always refetch on open so internal notes (e.g. Cleo) are up to date immediately — not a stale list cache.
      refetchOnMount: "always",
      // While scheduled, poll; faster when Cleo reports an active on-site session.
      refetchInterval: (q) => {
        const d = q.state.data;
        if (normalizeSvrStatusKey(d?.status) !== "scheduled") return false;
        return d.cleoFieldSessionActive ? 5_000 : 12_000;
      },
    }
  );
  const visitId = visit?.id ?? 0;
  const linkedCustomerId = visit?.customer?.id ?? 0;
  const { data: customerSiteRows } = trpc.customerSites.list.useQuery(
    { customerId: linkedCustomerId },
    { enabled: linkedCustomerId > 0, retry: false },
  );

  useEffect(() => {
    if (visit) setExpandedStageId(statusToStageId(visit.status));
  }, [visit?.id, visit?.status]);

  useEffect(() => {
    if (!visit) return;
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("matchCustomer") === "1" && visit.isCustomerUnmatched) {
      setShowMatchCustomerModal(true);
    }
  }, [visit?.id, visit?.isCustomerUnmatched]);

  const { data: salesStaff } = trpc.customers.listSalesStaff.useQuery(undefined, { enabled: !!user && (user.role === "admin") });
  const { data: customerSearchResultsRaw } = trpc.customers.search.useQuery(
    { query: matchQuery },
    { enabled: showMatchCustomerModal && matchTab === "existing" && matchQuery.trim().length > 1 }
  );
  const customerSearchResults = fullCustomerSearchHits(customerSearchResultsRaw);
  const { data: svrMedia } = trpc.siteVisits.getMedia.useQuery({ svrId: visitId }, { enabled: visitId > 0 });
  const { data: allUsers } = trpc.admin.listUsers.useQuery(undefined, { enabled: isAdmin });
  const { data: visitAssignees, refetch: refetchAssignees } = trpc.siteVisits.getAssignees.useQuery({ visitId }, { enabled: visitId > 0 });

  // Auto-scroll to Finance section when ?costing=1 is in URL
  useEffect(() => {
    if (needsCostingBanner && financeSectionRef.current) {
      // Small delay to ensure the page has rendered fully
      const timer = setTimeout(() => {
        financeSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [needsCostingBanner, visit?.id]);

  // Pre-fill form when data loads
  useEffect(() => {
    if (visit) {
      setInternalNotesDirty(false);
      setInternalNotes(visit.internalNotes ?? "");
      setFindingsAndActions(visit.findingsAndActions ?? "");
      setCustomerReport(visit.customerReport ?? "");
      setPartsCost(visit.partsCost ? String(visit.partsCost) : "");
      if (visit.billing?.rate) setSelectedRate(visit.billing.rate);
      if (visit.billing?.notes) setBillingNotes(visit.billing.notes ?? "");
      if (visit.billing?.paymentLink) setPaymentLink(visit.billing.paymentLink);
      if (visit.billing?.proposalRef) setProposalRef(visit.billing.proposalRef ?? "");
      // Pre-fill invoice amount from billing.rate (Finance/Admin can override)
      if (visit.billing?.rate) setInvoiceAmount(formatSvrBillingAmountDisplay(visit.billing.rate));
      // Pre-fill invoice description ONLY from Final Report (customerReport) — never from internalNotes
      if (visit.billing?.invoiceStatus === "pending") {
        const dateStr = formatSingaporeDate(visit.visitDate, {
          day: "numeric", month: "long", year: "numeric",
        }, "en-GB");
        // Use ONLY the Final Report (customerReport). internalNotes are staff-only and must NOT appear in invoices.
        const finalReport = visit.customerReport || "";
        const prefilled = finalReport
          ? `Service visit on ${dateStr}\n\n${finalReport}`
          : `Service visit on ${dateStr}`;
        setInvoiceDescription(prefilled);
      }
      // Pre-fill customer edit form
      if (visit.customer) {
        setEditName(visit.customer.name ?? "");
        setEditContact(visit.customer.contact ?? "");
        setEditEmail(visit.customer.email ?? "");
        setEditAddress(visit.customer.address ?? "");
        setEditUnitNumber(visit.customer.unitNumber ?? "");
        setEditPostalCode(visit.customer.postalCode ?? "");
        setEditSalespersonId(visit.customer.salespersonId);
      }
      if (visit.isCustomerUnmatched) {
        setNewCustomerName((visit.unmatchedRawName as string) ?? "");
        setNewCustomerPhone((visit.unmatchedRawPhone as string) ?? "");
        const rawAddress = (visit.unmatchedRawAddress as string) ?? "";
        setNewCustomerAddress(rawAddress);
        setNewCustomerAddressQuery(rawAddress);
      }
    }
  }, [visit?.id]);

  // Sync internal notes when the server value changes (Cleo appends) while the staff member is not mid-edit in this field.
  useEffect(() => {
    if (!visit) return;
    if (internalNotesDirty) return;
    setInternalNotes(visit.internalNotes ?? "");
  }, [visit?.internalNotes, visit?.id, internalNotesDirty]);

  const updateCustomer = trpc.customers.update.useMutation({
    onSuccess: () => {
      toast.success("Customer updated");
      setEditingCustomer(false);
      utils.siteVisits.getById.invalidate({ id: visitId });
    },
    onError: (err: { message: string }) =>
      toast.error(mapCustomerUpdateErrorMessage(err.message || "Failed to update customer")),
  });



  const submitUpdate = trpc.siteVisits.submitUpdate.useMutation({
    onMutate: (vars) => {
      devLog("[SVR][UI] Save and Proceed clicked", {
        visitId: vars.id,
        currentStatus: visit?.status,
        hasInternalNotes: Boolean(vars.internalNotes?.trim()),
        hasCustomerReport: Boolean(vars.customerReport?.trim() || vars.findingsAndActions?.trim()),
      });
    },
    onSuccess: async () => {
      devLog("[SVR][UI] submitUpdate success", {
        visitId,
        oldStatus: visit?.status,
        expectedNewStatus:
          normalizeSvrStatusKey(visit?.status) === "scheduled" ? "pending_review" : visit?.status,
      });
      setInternalNotesDirty(false);
      setFocusedActionError(null);
      toast.success("Update submitted for PIC review");
      await utils.siteVisits.getById.invalidate({ id: visitId });
      await refetchVisit();
    },
    onError: (e, vars) => {
      console.error("[SVR][UI] submitUpdate failed", {
        visitId: vars.id,
        oldStatus: visit?.status,
        error: e.message,
      });
      setFocusedActionError(e.message);
      toast.error(e.message);
    },
  });

  const saveCustomerReport = trpc.siteVisits.saveCustomerReport.useMutation({
    onSuccess: () => {
      utils.siteVisits.getById.invalidate({ id: visitId });
    },
    onError: (e) => toast.error("Auto-save failed: " + e.message),
  });
  const polishReport = trpc.siteVisits.polishReport.useMutation({
    onSuccess: (data) => {
      if (data.report) {
        setCustomerReport(data.report);
        // Auto-save the polished report to DB immediately using saveCustomerReport
        // (works for ALL statuses including closed/waived/report_sent — unlike submitUpdate)
        saveCustomerReport.mutate({ id: visitId, customerReport: data.report });
      } else {
        toast.error("AI polish produced empty output.");
        return;
      }
      toast.success("Report polished — saved to record");
    },
    onError: (e) => toast.error(e.message || "AI polish failed"),
  });

  const picAction = trpc.siteVisits.picAction.useMutation({
    onSuccess: (_, vars) => {
      setFocusedActionError(null);
      toast.success(vars.action === "close" ? "Visit closed" : "Report sent to customer");
      utils.siteVisits.getById.invalidate({ id: visitId });
    },
    onError: (e) => {
      setFocusedActionError(e.message);
      toast.error(e.message);
    },
  });

  const setPendingBilling = trpc.siteVisits.setPendingBilling.useMutation({
    onSuccess: () => {
      toast.success("Billing decision saved");
      setBillingDecision("");
      setAgreedRate("");
      setBillingNote("");
      utils.siteVisits.getById.invalidate({ id: visitId });
    },
    onError: (e) => toast.error(e.message),
  });

  const requestWaiver = trpc.siteVisits.requestWaiver.useMutation({
    onSuccess: () => {
      toast.success("Waiver request submitted");
      setShowWaiverForm(false);
      utils.siteVisits.getById.invalidate({ id: visitId });
    },
    onError: (e) => toast.error(e.message),
  });

  const decideWaiver = trpc.siteVisits.decideWaiver.useMutation({
    onSuccess: (_, vars) => {
      toast.success(vars.approved ? "Waiver approved" : "Waiver rejected");
      utils.siteVisits.getById.invalidate({ id: visitId });
    },
    onError: (e) => toast.error(e.message),
  });

  const updateBilling = trpc.siteVisits.updateBilling.useMutation({
    onSuccess: () => {
      toast.success("Billing updated");
      utils.siteVisits.getById.invalidate({ id: visitId });
    },
    onError: (e) => toast.error(e.message),
  });

  const uploadInvoice = trpc.siteVisits.uploadInvoice.useMutation({
    onSuccess: () => {
      toast.success("Invoice uploaded");
      utils.siteVisits.getById.invalidate({ id: visitId });
    },
    onError: (e) => toast.error(e.message),
  });

  // Finance invoice state
  const [invoiceDescription, setInvoiceDescription] = useState("");
  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [paymentLink, setPaymentLink] = useState("");
  const [proposalRef, setProposalRef] = useState("");
  const [showInvoiceConfirm, setShowInvoiceConfirm] = useState(false);
  const [sendInvoiceConfirm, setSendInvoiceConfirm] = useState<"whatsapp" | "email" | "both" | null>(null);
  const [waiveConfirmOpen, setWaiveConfirmOpen] = useState(false);
  const [svrWhatsAppConfirmOpen, setSvrWhatsAppConfirmOpen] = useState(false);
  const [svrReminderConfirmOpen, setSvrReminderConfirmOpen] = useState(false);
  const svrWhatsAppIdempotencyKeyRef = useRef<string>("");
  const [svrInvoiceReviseOpen, setSvrInvoiceReviseOpen] = useState(false);
  const [svrReviseSentWarningOpen, setSvrReviseSentWarningOpen] = useState(false);

  const generateInvoice = trpc.siteVisits.generateInvoice.useMutation({
    onSuccess: () => {
      toast.success("Invoice generated — please review and confirm");
      setShowInvoiceConfirm(false);
      setSvrInvoiceReviseOpen(false);
      setSvrReviseSentWarningOpen(false);
      setSvrWhatsAppConfirmOpen(false);
      utils.siteVisits.getById.invalidate({ id: visitId });
    },
    onError: (e) => toast.error(e.message),
  });

  const confirmInvoice = trpc.siteVisits.confirmInvoice.useMutation({
    onSuccess: () => {
      toast.success("Invoice confirmed — PIC has been notified");
      utils.siteVisits.getById.invalidate({ id: visitId });
    },
    onError: (e) => toast.error(e.message),
  });

  const [confirmMarkPaidOpen, setConfirmMarkPaidOpen] = useState(false);
  const markPaid = trpc.siteVisits.markPaid.useMutation({
    onSuccess: (data) => {
      toast.success(data?.message ?? "Payment recorded.");
      if (data?.receipt?.warning) {
        toast.warning(data.receipt.warning, { duration: 8000 });
      }
      for (const w of data?.warnings ?? []) {
        if (w && w !== data?.receipt?.warning && w !== data?.message) {
          toast.warning(w, { duration: 8000 });
        }
      }
      setConfirmMarkPaidOpen(false);
      utils.siteVisits.getById.invalidate({ id: visitId });
    },
    onError: (e) => toast.error(e.message),
  });

  const waiveSvrCharge = trpc.siteVisits.waiveSvrCharge.useMutation({
    onSuccess: () => {
      toast.success("Charge waived");
      setWaiveConfirmOpen(false);
      utils.siteVisits.getById.invalidate({ id: visitId });
      if (typeof window !== "undefined" && window.confirm("Charge waived. Do you want to close this case?")) {
        setLocation("/site-visits");
      }
    },
    onError: (e) => toast.error(e.message),
  });

  const sendInvoice = trpc.siteVisits.sendInvoice.useMutation({
    onSuccess: (data, vars) => {
      if (vars.channel === "whatsapp") {
        const status = (data as { staffStatus?: string })?.staffStatus;
        toast.success(status === "awaiting_response"
          ? "Resume request sent — awaiting the customer’s YES before sending the invoice"
          : vars.action === "reminder"
          ? "Payment reminder submitted via WhatsApp"
          : status === "submitted" ? "Invoice submitted via WhatsApp" : "Invoice send requested via WhatsApp");
        setSvrWhatsAppConfirmOpen(false);
        setSvrReminderConfirmOpen(false);
      } else {
        toast.success("Invoice sent via email");
      }
      setSendInvoiceConfirm(null);
      utils.siteVisits.getById.invalidate({ id: visitId });
    },
    onError: (e) => {
      toast.error(e.message || "Failed to send invoice");
      setSendInvoiceConfirm(null);
    },
  });
  const scheduleInvoiceSend = trpc.siteVisits.scheduleInvoiceSend.useMutation({
    onSuccess: () => {
      toast.success("Invoice scheduled for night-before reminder");
      utils.siteVisits.getById.invalidate({ id: visitId });
    },
    onError: (e) => toast.error(e.message),
  });

  const updateVisit = trpc.siteVisits.update.useMutation({
    onSuccess: (data) => {
      toast.success("Visit updated");
      toastSvrNotificationWarnings(toast, data.notificationWarnings);
      utils.siteVisits.getById.invalidate({ id: visitId });
      utils.siteVisits.teamCalendarVisits.invalidate();
      utils.siteVisits.calendarVisits.invalidate();
    },
    onError: (e) => {
      setFocusedActionError(e.message);
      toast.error(e.message);
    },
  });

  const setAssignees = trpc.siteVisits.setAssignees.useMutation({
    onSuccess: (data) => {
      toast.success("Assignees saved");
      toastSvrNotificationWarnings(toast, data.notificationWarnings);
      setShowAssigneePanel(false);
      refetchAssignees();
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteVisit = trpc.siteVisits.delete.useMutation({
    onSuccess: (data) => {
      toast.success(data.alreadyCancelled ? "Visit was already cancelled" : "Visit cancelled");
      toastSvrNotificationWarnings(toast, data.notificationWarnings);
      setCancelDialogOpen(false);
      setCancellationReason("");
      utils.siteVisits.getById.invalidate({ id: visitId });
    },
    onError: (e) => toast.error(e.message),
  });
  const linkCustomer = trpc.siteVisits.linkCustomer.useMutation({
    onSuccess: async () => {
      toast.success("Customer linked successfully.");
      setShowMatchCustomerModal(false);
      setSelectedCustomerId(null);
      setMatchQuery("");
      await utils.siteVisits.getById.invalidate({ id: visitId });
      await utils.dashboard.getRoleBasedTasks.invalidate();
      await utils.dashboard.getUnmatchedCustomers.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  const createAndLinkCustomer = trpc.siteVisits.createAndLinkCustomer.useMutation({
    onSuccess: async () => {
      toast.success("Customer created and linked.");
      setShowMatchCustomerModal(false);
      setNewCustomerPostalCode("");
      setNewCustomerUnitNumber("");
      setNewCustomerIsCondo(false);
      setNewCustomerAddressQuery("");
      await utils.siteVisits.getById.invalidate({ id: visitId });
      await utils.dashboard.getRoleBasedTasks.invalidate();
      await utils.dashboard.getUnmatchedCustomers.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  // Reschedule state (all roles) — free-form start/end time
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleStart, setRescheduleStart] = useState("09:00");
  const [rescheduleEnd, setRescheduleEnd] = useState("17:00");

  const [sendConfirm, setSendConfirm] = useState<"whatsapp" | "email" | null>(null);
  const [highlightedStageId, setHighlightedStageId] = useState<string | null>(null);
  const sendReport = trpc.siteVisits.sendReport.useMutation({
    onSuccess: (_, vars) => {
      toast.success(vars.channel === "whatsapp" ? "Report sent via WhatsApp" : "Report sent via email");
      setSendConfirm(null);
      utils.siteVisits.getById.invalidate({ id: visitId });
    },
    onError: (e) => {
      toast.error(e.message || "Failed to send report");
      setSendConfirm(null);
    },
  });

  const handleInvoiceUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(",")[1];
      uploadInvoice.mutate({ visitId, filename: file.name, base64 });
    };
    reader.readAsDataURL(file);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    const taskOpenErr =
      historyState && typeof historyState === "object" && historyState !== null && "taskOpen" in historyState
        ? (historyState as { taskOpen?: unknown }).taskOpen
        : undefined;
    console.warn("[admin-tasks] service visit load error", {
      routeId,
      routeLookupId: String(routeLookupId),
      attemptedRoute: `/site-visits/id/${routeId}`,
      taskOpen: taskOpenErr,
      message: error.message,
    });
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto py-16 text-center">
          <AlertTriangle className="h-7 w-7 text-red-500 mx-auto mb-3" />
          <p className="text-sm font-semibold text-red-700">Failed to load service visit</p>
          <p className="text-xs text-muted-foreground mt-1">{error.message || "Please retry."}</p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <Button variant="outline" onClick={() => refetchVisit()}>
              Retry
            </Button>
            <Button variant="ghost" onClick={goBack}>
              Back
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!visit) {
    const taskOpen =
      historyState && typeof historyState === "object" && historyState !== null && "taskOpen" in historyState
        ? (historyState as { taskOpen?: unknown }).taskOpen
        : undefined;
    console.warn("[admin-tasks] service visit not found after navigation", {
      routeId,
      routeLookupId: String(routeLookupId),
      attemptedRoute: `/site-visits/id/${routeId}`,
      taskOpen,
      reason: "no_row_or_invalid_id",
    });
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto py-16 text-center">
          <p className="text-muted-foreground">Service visit not found.</p>
          <Button variant="outline" className="mt-4" onClick={goBack}>
            Back
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const visitStatusKey = normalizeSvrStatusKey(visit.status);
  const isCancelled = visitStatusKey === "cancelled";
  const customer = visit.customer;
  const billing = visit.billing;
  const invoicePdfPath = resolveSvrInvoicePdfPath({
    invoiceToken: billing?.invoiceToken,
    legacyInvoiceUrl: billing?.invoiceUrl,
  });
  const isFinance = user?.role === "finance" || isAdmin;
  const isSales = user?.role === "sales" || isAdmin;
  const isTechnician = user?.role === "service" || user?.role === "programming" || user?.role === "installer" || isAdmin;
  const canManageCustomerSvrLink = isAdmin || isSales || isTechnician || isFinance;
  const isMyVisit =
    isAdmin ||
    visit.technicianId === user?.id ||
    (visitAssignees?.some((a) => a.userId === user?.id) ?? false);
  const canEditVisitSchedule = isAdmin || isMyVisit;
  const isMyPicVisit = visit.picId === user?.id || isAdmin;
  // Auto-closed on technician done; billing decision still to be set by Finance / PIC.
  const isBillingPending =
    visitStatusKey === "closed" &&
    Boolean((visit as { billingPending?: boolean | null }).billingPending) &&
    !(visit as { billingDecision?: string | null }).billingDecision;
  const canSetPendingBilling = isBillingPending && (isFinance || isMyPicVisit);
  const canEditVisitUpdate = isAdmin || isMyPicVisit || (isTechnician && isMyVisit);
  const customerSvrLink = (visit as any).trackingToken
    ? buildSvrTrackingUrl(String((visit as any).trackingToken))
    : null;

  const headerCustomerName =
    customer?.name?.trim() ||
    (visit as { unmatchedRawName?: string | null }).unmatchedRawName?.trim() ||
    "—";
  const unmatchedRawAddress =
    String((visit as { unmatchedRawAddress?: string | null }).unmatchedRawAddress ?? "").trim() || null;
  const fullAddress =
    formatCustomerAddressLine({
      unitNumber: customer?.unitNumber,
      address: customer?.address,
      postalCode: customer?.postalCode,
    }) ?? unmatchedRawAddress;
  const headerAddressLine = fullAddress;

  const visitDateStr = formatSingaporeDate(visit.visitDate, {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  }, "en-GB");
  const isClosedWorkflow = visitStatusKey === "closed";
  const currentStageId = isClosedWorkflow ? "close" : statusToStageId(visitStatusKey);
  const currentStageIdx = WORKFLOW_STAGES.findIndex((stage) => stage.id === currentStageId);
  /** Closed/waived = terminal — spine must not show the last stage as "In progress"; all steps including Close are completed. */
  const workflowComplete = visitStatusKey === "closed" || visitStatusKey === "waived" || isCancelled;
  const spineCurrentIdx = workflowComplete ? WORKFLOW_STAGES.length : currentStageIdx;

  const scrollToStage = (stageId: string) => {
    setExpandedStageId(stageId as SvrExpandedStageId);
    const target = document.getElementById(stageId);
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    setHighlightedStageId(stageId);
    window.setTimeout(() => {
      setHighlightedStageId((prev) => (prev === stageId ? null : prev));
    }, 1200);
  };

  const servicePast = isClosedWorkflow || ["pending_review", "report_sent", "awaiting_payment", "closed", "waived"].includes(visitStatusKey);
  const serviceActive = !isClosedWorkflow && visitStatusKey === "scheduled";
  const serviceStatusSub =
    servicePast && !serviceActive
      ? (visit as { updateSubmittedAt?: Date | string | null }).updateSubmittedAt
        ? `Completed on ${formatDateTime((visit as { updateSubmittedAt?: Date | string | null }).updateSubmittedAt!)}`
        : "Completed"
      : undefined;

  const picPast = isClosedWorkflow || ["report_sent", "awaiting_payment", "closed", "waived"].includes(visitStatusKey);
  const picActive = !isClosedWorkflow && visitStatusKey === "pending_review";
  const picStatusSub =
    picPast && !picActive
      ? visit.reportSentAt
        ? `Completed on ${formatDateTime(visit.reportSentAt)}`
        : "Completed"
      : undefined;

  const financePast = isClosedWorkflow || ["closed", "waived"].includes(visitStatusKey);
  const financeActive = !isClosedWorkflow && ["report_sent", "awaiting_payment"].includes(visitStatusKey);
  const financeStatusSub =
    financePast && !financeActive
      ? billing?.paidAt
        ? `Completed on ${formatDateTime(billing.paidAt)}`
        : billing?.raisedAt
          ? `Raised on ${formatDateTime(billing.raisedAt)}`
          : "Completed"
      : undefined;

  const copyVisitNumber = () => {
    void navigator.clipboard.writeText(visit.visitNumber);
    toast.success("Ticket number copied");
  };

  const backLabel = readBackContext().label;
  const showFinancials = canViewSvrFinancialDetails(user?.role);
  const linkedSite = customerSiteRows?.find((site) => site.id === (visit as { siteId?: number | null }).siteId) ?? null;
  const technicianLabel =
    visit.technicianName?.trim()
    || (visitAssignees && visitAssignees.length > 0
      ? visitAssignees.map((assignee) => assignee.userName).join(", ")
      : "")
    || "";
  const shortVisitDate = formatSingaporeDate(
    visit.visitDate,
    { day: "numeric", month: "short", year: "numeric" },
    "en-GB",
  );
  const arrivalLabel = visit.arrivalWindow ? formatArrivalWindow(String(visit.arrivalWindow)) : "";
  const customerConfirmed = Boolean(customer?.id) && Boolean(fullAddress) && !visit.isCustomerUnmatched;
  const focused = buildSvrFocusedWorkflow({
    status: visitStatusKey,
    technicianAssigned: Boolean(technicianLabel),
    cleoFieldSessionActive: Boolean(visit.cleoFieldSessionActive),
    arrivedAt: (visit as { arrivedAt?: string | Date | null }).arrivedAt,
    departedAt: (visit as { departedAt?: string | Date | null }).departedAt,
    hasWorkNotes: Boolean(internalNotes.trim() || customerReport.trim() || visit.internalNotes?.trim() || visit.findingsAndActions?.trim()),
    hasCustomerReport: Boolean(customerReport.trim() || visit.customerReport?.trim()),
    updateSubmitted: Boolean((visit as { updateSubmittedAt?: string | Date | null }).updateSubmittedAt),
    reportSent: Boolean(visit.reportSentAt),
    picAssigned: Boolean(visit.picName || visit.picId),
    customerUnmatched: Boolean(visit.isCustomerUnmatched),
    scheduleReady: Boolean(parseValidArrivalWindow(visit.arrivalWindow)),
    billable: Boolean(visit.billable),
    invoicePaid: Boolean(billing?.invoiceStatus === "paid" || billing?.paidAt),
  });
  const activityEvents = buildSvrActivityEvents(
    {
      createdAt: visit.createdAt,
      createdBy: visit.createdBy,
      custWhatsappSentAt: (visit as { custWhatsappSentAt?: string | Date | null }).custWhatsappSentAt,
      custWhatsappSentBy: (visit as { custWhatsappSentBy?: string | null }).custWhatsappSentBy,
      arrivedAt: (visit as { arrivedAt?: string | Date | null }).arrivedAt,
      departedAt: (visit as { departedAt?: string | Date | null }).departedAt,
      updateSubmittedAt: (visit as { updateSubmittedAt?: string | Date | null }).updateSubmittedAt,
      updateSubmittedBy: (visit as { updateSubmittedBy?: string | null }).updateSubmittedBy,
      reportSentAt: visit.reportSentAt,
      reportSentBy: visit.reportSentBy,
      closedAt: visit.closedAt,
      closedBy: visit.closedBy,
      autoClosedAt: (visit as { autoClosedAt?: string | Date | null }).autoClosedAt,
      cancelledAt: (visit as { cancelledAt?: string | Date | null }).cancelledAt,
      cancelledByName: (visit as { cancelledByName?: string | null }).cancelledByName,
      waiverRequestedAt: visit.waiverRequestedAt,
      waiverRequestedBy: visit.waiverRequestedBy,
      paidAt: showFinancials ? billing?.paidAt : null,
      paidBy: showFinancials ? billing?.paidBy : null,
    },
    { includeFinancial: showFinancials },
  );
  const headerMeta = [headerCustomerName, headerAddressLine, shortVisitDate, arrivalLabel || null, technicianLabel || null]
    .filter((part): part is string => Boolean(part && String(part).trim() && part !== "—"));

  const submitVisitUpdate = () => {
    const payload = {
      id: visitId,
      internalNotes: internalNotes || undefined,
      customerReport: customerReport || undefined,
      findingsAndActions: customerReport || internalNotes || undefined,
      partsCost: partsCost || undefined,
    };
    devLog("[SVR][UI] submitUpdate mutation sent", {
      visitId,
      oldStatus: visitStatusKey,
      expectedNewStatus: visitStatusKey === "scheduled" ? "closed" : visitStatusKey,
    });
    submitUpdate.mutate(payload);
  };

  const runFocusedPrimary = () => {
    if (focused.primaryKind === "submit-update") {
      submitVisitUpdate();
      return;
    }
    const stage =
      focused.primaryKind === "focus-pic" ? "pic"
      : focused.primaryKind === "focus-finance" ? "finance"
      : focused.primaryKind === "focus-close" ? "close"
      : "service";
    scrollToStage(stage);
    if (focused.primaryKind === "focus-notes") {
      window.setTimeout(() => document.getElementById("svr-internal-notes")?.focus(), 80);
    }
    if (focused.primaryKind === "focus-finance") {
      window.setTimeout(() => financeSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
  };

  return (
    <DashboardLayout>
      <div className="min-h-[calc(100vh-4rem)] w-full bg-slate-100/60">
        {/* — Full-width top header — ticket + status (SVR) */}
        <div className="border-b border-slate-200/80 bg-white px-4 py-4 md:px-8">
          <button
            type="button"
            data-testid="svr-back"
            onClick={goBack}
            className="flex items-center gap-1 text-sm text-slate-500 transition-colors hover:text-slate-800"
          >
            <ChevronLeft className="h-4 w-4" />
            {backLabel}
          </button>
          <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h1 className="font-mono text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                {visit.visitNumber}
              </h1>
              <p data-testid="svr-header-meta" className="mt-2 text-sm text-slate-500">
                {headerMeta.map((part, index) => (
                  <span key={`${part}-${index}`}>
                    {index > 0 ? " · " : null}
                    {index === 0 && customer?.id ? (
                      <button
                        type="button"
                        className="hover:underline"
                        onClick={() => setLocation(`/customers/id/${customer.id}`)}
                      >
                        {part}
                      </button>
                    ) : (
                      part
                    )}
                  </span>
                ))}
              </p>
              {visit.visitType === "urgent" && (
                <p className="mt-2 text-xs font-semibold text-red-700">Urgent</p>
              )}
              {visitStatusKey === "pending_review" && (
                <div className="mt-2 text-xs">
                  {visit.picName ? (
                    <p>
                      <span className="font-medium text-amber-800">Pending review from {visit.picName}</span>
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedPicId(visit.picId ?? "");
                            setEditingPic(true);
                          }}
                          className="ml-2 text-blue-600 hover:underline"
                        >
                          Reassign
                        </button>
                      )}
                    </p>
                  ) : (
                    <div>
                      <p className="font-semibold text-red-700">Unassigned — no PIC</p>
                      {(isAdmin || isSales) && !editingPic && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedPicId(isAdmin ? "" : (user?.id ?? ""));
                            setEditingPic(true);
                          }}
                          className="mt-1 text-blue-600 hover:underline"
                        >
                          {isAdmin ? "Assign PIC" : "Claim as my SVR"}
                        </button>
                      )}
                    </div>
                  )}
                  {(isAdmin || isSales) && editingPic && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {isAdmin ? (
                        <select
                          value={selectedPicId}
                          onChange={(e) => setSelectedPicId(e.target.value ? parseInt(e.target.value) : "")}
                          className="border border-input rounded-md px-2 py-1 text-xs bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                        >
                          <option value="">— Select PIC —</option>
                          {salesStaff?.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          Assign to: <strong>{user?.name}</strong>
                        </span>
                      )}
                      <button
                        type="button"
                        disabled={!selectedPicId || updateVisit.isPending}
                        onClick={() => {
                          if (!selectedPicId) return;
                          updateVisit.mutate(
                            { id: visitId, picId: selectedPicId as number },
                            { onSuccess: () => { setEditingPic(false); toast.success("PIC assigned"); } }
                          );
                        }}
                        className="rounded bg-slate-900 px-2 py-1 text-xs text-white disabled:opacity-50"
                      >
                        {updateVisit.isPending ? "Saving…" : "Save"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingPic(false)}
                        className="px-2 py-1 text-xs text-muted-foreground"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              )}
              {isTechnician && (visit as any).cleoPaymentReminder && (
                <div className="mt-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">
                  {(visit as any).cleoPaymentReminder}
                </div>
              )}
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={copyVisitNumber}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
                title="Copy ticket number"
              >
                <Copy className="h-4 w-4" />
              </button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 gap-1.5 rounded-full px-3 text-xs font-medium text-slate-700"
                onClick={() =>
                  toast.info("Use the “Report an issue” control in the app corner to file a report.", { duration: 4000 })
                }
              >
                <Flag className="h-3.5 w-3.5" />
                Report issue
              </Button>
              <span
                data-testid="svr-status-badge"
                className="inline-flex items-center rounded-full bg-violet-100 px-3 py-1.5 text-xs font-medium text-violet-800"
              >
                {focused.headerBadge}
              </span>
              {isAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="outline" size="icon" className="h-8 w-8 shrink-0" aria-label="More actions">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {!isCancelled && <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => setCancelDialogOpen(true)}
                    >
                      Cancel service visit
                    </DropdownMenuItem>}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          <div className="mt-5 border-t border-slate-100 pt-4 md:hidden" data-testid="svr-focused-workflow-mobile">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Focused Workflow</p>
            <div className="flex items-stretch justify-between gap-0.5 overflow-x-auto pb-1">
              {SVR_FOCUSED_STAGES.map((stage, idx) => {
                const isPast = idx < spineCurrentIdx;
                const isCurrent = idx === spineCurrentIdx;
                return (
                  <button
                    key={stage.id}
                    type="button"
                    onClick={() => scrollToStage(stage.id)}
                    className="flex min-w-0 flex-1 flex-col items-center gap-1.5 border-l border-slate-100 pl-1 first:border-l-0 first:pl-0"
                  >
                    <span
                      className={cn(
                        "flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold",
                        isCurrent && "bg-violet-600 text-white",
                        isPast && "bg-emerald-500 text-white",
                        !isCurrent && !isPast && "bg-slate-100 text-slate-500",
                      )}
                    >
                      {isPast ? <Check className="h-3 w-3" strokeWidth={3} /> : idx + 1}
                    </span>
                    <span
                      className={cn(
                        "w-full text-center text-[9px] font-bold leading-tight",
                        isPast && "text-emerald-800",
                        isCurrent && "text-violet-800",
                        !isPast && !isCurrent && "text-slate-400"
                      )}
                    >
                      {stage.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mx-auto flex w-full max-w-[1560px] items-start">
        {/* — Desktop: left workflow spine in card — */}
        <div className="hidden w-[300px] min-w-[300px] shrink-0 self-stretch border-r border-slate-200/80 bg-white md:block">
          <div className="sticky top-0 px-5 py-7">
            <p className="mb-4 px-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
              Focused Workflow
            </p>
            <div data-testid="svr-focused-workflow" className="relative">
              {SVR_FOCUSED_STAGES.map((stage, idx) => {
                const isPast = idx < spineCurrentIdx;
                const isCurrent = idx === spineCurrentIdx;
                return (
                  <div key={stage.id} className="relative">
                    {idx < SVR_FOCUSED_STAGES.length - 1 && (
                      <div className="absolute left-[15px] top-8 h-[calc(100%-8px)] w-px bg-slate-200" aria-hidden />
                    )}
                    <button
                      type="button"
                      onClick={() => scrollToStage(stage.id)}
                      className="relative flex w-full items-start gap-3 py-2.5 text-left"
                    >
                      <span
                        className={cn(
                          "z-[1] flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                          isCurrent && "bg-violet-600 text-white",
                          isPast && "bg-emerald-500 text-white",
                          !isCurrent && !isPast && "bg-slate-100 text-slate-500",
                        )}
                      >
                        {isPast ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : idx + 1}
                      </span>
                      <span className="min-w-0 pt-0.5">
                        <span className={cn("block text-sm font-semibold", isCurrent ? "text-violet-800" : "text-slate-800")}>
                          {stage.label}
                        </span>
                        <span className="mt-0.5 block text-xs leading-snug text-slate-500">{stage.description}</span>
                        {isPast && <span className="mt-1 block text-[11px] text-emerald-700">Completed</span>}
                        {isCurrent && (
                          <span data-testid="svr-current-stage" className="mt-1 block text-[11px] font-medium text-violet-700">
                            {focused.currentStage}
                          </span>
                        )}
                      </span>
                    </button>
                  </div>
                );
              })}
              <p className="mt-4 flex items-start gap-1.5 text-[10px] leading-relaxed text-slate-500">
                <Info className="h-3 w-3 shrink-0 mt-0.5" />
                <span>
                  {workflowComplete
                    ? "This visit is completed — all stages including Close are done."
                    : "The highlighted stage is the current step. Select a stage to jump to its actions."}
                </span>
              </p>
            </div>
            {isAdmin && !isCancelled && (
              <div className="mt-3 px-0">
                <button
                  type="button"
                  onClick={() => setCancelDialogOpen(true)}
                  disabled={deleteVisit.isPending}
                  className="text-left text-xs text-red-600 hover:underline disabled:opacity-50"
                >
                  {deleteVisit.isPending ? "Cancelling…" : "Cancel this visit (admin)"}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* — Main content — */}
        <div className="min-w-0 flex-1 space-y-5 bg-slate-50/70 py-6 pl-4 pr-4 sm:px-6 md:px-8 lg:px-10">

        {isCancelled && (
          <Card className="border-red-200 bg-red-50">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-red-900">
                <XCircle className="h-5 w-5" /> Visit cancelled
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-2 text-sm text-red-950 sm:grid-cols-2">
              <div><span className="font-semibold">Cancelled by:</span> {(visit as any).cancelledByName || "Unknown"}</div>
              <div><span className="font-semibold">Cancelled at:</span> {(visit as any).cancelledAt ? formatDateTime((visit as any).cancelledAt) : "Unknown"}</div>
              <div><span className="font-semibold">Cancelled via:</span> {(visit as any).cancellationSource === "portal_svr_workspace" ? "Clarity Portal — SVR workspace" : ((visit as any).cancellationSource || "Unknown")}</div>
              <div className="sm:col-span-2"><span className="font-semibold">Reason:</span> {(visit as any).cancellationReason || "No reason recorded"}</div>
            </CardContent>
          </Card>
        )}

        {/* ── CUSTOMER WHATSAPP NOTIFICATION BANNER — manual send/resend control ── */}
        {visitStatusKey === "scheduled" && [undefined, null, "pending", "failed"].includes((visit as any).custWhatsappStatus) && (isAdmin || isSales || isTechnician) && (
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-300 text-amber-900 rounded-lg px-4 py-3 shadow-sm">
            <div className="shrink-0 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
              <MessageCircle className="h-4 w-4 text-amber-700" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm">Customer not yet notified</p>
              <p className="text-xs text-amber-700 mt-0.5">Send a WhatsApp confirmation to the customer about this visit.</p>
            </div>
            <CustomerWhatsAppButton visitId={visit.id} onSuccess={() => refetchVisit()} alreadySent={false} />
          </div>
        )}
        {visitStatusKey === "scheduled" && (visit as any).custWhatsappStatus === "sent" && (isAdmin || isSales || isTechnician) && (
          <div className="flex items-center gap-3 bg-green-50 border border-green-300 text-green-900 rounded-lg px-4 py-3 shadow-sm">
            <div className="shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <MessageCircle className="h-4 w-4 text-green-700" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm">Customer has been notified</p>
              <p className="text-xs text-green-700 mt-0.5">Use resend if you need to send the WhatsApp notification again.</p>
            </div>
            <CustomerWhatsAppButton visitId={visit.id} onSuccess={() => refetchVisit()} alreadySent />
          </div>
        )}

        {/* Costing needed banner — shown when navigated from Tasks with ?costing=1 */}
        {needsCostingBanner && (
          <div className="flex items-center gap-3 bg-red-600 text-white rounded-lg px-4 py-3 shadow-md">
            <div className="shrink-0 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <DollarSign className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-sm uppercase tracking-wide">⚠ COSTING NEEDS TO BE ADDED</p>
              <p className="text-xs text-red-100 mt-0.5">This service visit is billable. Please scroll down to the Finance section to set the billing rate and raise an invoice.</p>
            </div>
            <button
              onClick={() => financeSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="shrink-0 text-xs font-semibold bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-md transition-colors"
            >
              Go to Finance ↓
            </button>
          </div>
        )}

        {Boolean(visit.isCustomerUnmatched) && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="shrink-0 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 text-amber-700" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm">Unmatched</p>
                <p className="text-xs text-amber-700 mt-0.5">This visit is not linked to a customer yet.</p>
              </div>
              {isAdmin && (
                <Button
                  type="button"
                  size="sm"
                  className="bg-amber-700 text-white hover:bg-amber-800"
                  onClick={() => setShowMatchCustomerModal(true)}
                >
                  MATCH CUSTOMER
                </Button>
              )}
            </div>
            {(visit.unmatchedRawAddress as string | null) && (
              <p className="mt-2 text-xs text-amber-800">
                Raw submitted address: {(visit.unmatchedRawAddress as string).trim()}
              </p>
            )}
          </div>
        )}

        <section
          data-testid="svr-next-action"
          className="rounded-2xl bg-[#0f1c3f] px-5 py-5 text-white shadow-sm sm:px-6"
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">Next action</p>
              <p className="mt-2 text-xs text-slate-300">Current stage · {focused.currentStage}</p>
              <h2 className="mt-1 text-xl font-semibold tracking-tight sm:text-2xl">{focused.nextTitle}</h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-200">{focused.nextDetail}</p>
              {focused.completed.length > 0 && (
                <p className="mt-3 text-xs text-slate-300">Done: {focused.completed.join(" · ")}</p>
              )}
              {focused.blocker && (
                <p data-testid="svr-blocker" className="mt-3 text-sm font-medium text-amber-200">
                  {focused.blocker}
                </p>
              )}
              {focusedActionError && (
                <p data-testid="svr-action-error" className="mt-3 text-sm font-medium text-red-200" role="alert">
                  {focusedActionError}
                </p>
              )}
            </div>
            {focused.primaryLabel && (
              <Button
                type="button"
                data-testid="svr-primary-action"
                className="h-11 shrink-0 rounded-xl bg-white px-4 text-sm font-semibold tracking-wide text-slate-900 hover:bg-slate-100"
                onClick={runFocusedPrimary}
                disabled={focused.primaryKind === "submit-update" && (submitUpdate.isPending || (visitStatusKey === "scheduled" && Boolean(visit.cleoFieldSessionActive)) || (!internalNotes.trim() && !customerReport.trim()))}
              >
                <Play className="mr-1.5 h-4 w-4" />
                {submitUpdate.isPending && focused.primaryKind === "submit-update" ? "Saving..." : focused.primaryLabel}
              </Button>
            )}
          </div>
        </section>

        <div data-testid="svr-summary-grid" className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <section data-testid="svr-customer-site" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Customer &amp; site</p>
            <div className="mt-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-lg font-semibold text-slate-900">
                  {customer?.id ? (
                    <button type="button" className="hover:underline" onClick={() => setLocation(`/customers/id/${customer.id}`)}>
                      {headerCustomerName}
                    </button>
                  ) : headerCustomerName}
                </p>
                {linkedSite?.label && <p className="mt-1 text-sm text-slate-600">{linkedSite.label}</p>}
                {fullAddress && (
                  <p className="mt-2 flex items-start gap-1.5 text-sm text-slate-600">
                    <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span className="whitespace-pre-wrap">{fullAddress}</span>
                  </p>
                )}
                {customer?.contact && <p className="mt-2 text-sm text-slate-700">{customer.contact}</p>}
                {customer?.email && <p className="text-sm text-slate-500">{customer.email}</p>}
                {customer?.notes?.trim() && (
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">{customer.notes}</p>
                )}
              </div>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingCustomer(true);
                    setExpandedStageId("service");
                    window.setTimeout(() => document.getElementById("svr-edit-customer")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
                  }}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Edit
                </button>
              )}
            </div>
            {customerConfirmed && (
              <p data-testid="svr-customer-address-confirmed" className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Customer and address confirmed
              </p>
            )}
          </section>

          <section id="svr-appointment" data-testid="svr-appointment" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Appointment</p>
              {!!user && canEditVisitSchedule && !["closed", "waived", "report_sent"].includes(visitStatusKey) && !showReschedule && parseValidArrivalWindow((visit as any).arrivalWindow) ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => {
                    setRescheduleDate(toSingaporeDateInputValue(visit.visitDate));
                    const clocks = clockInputsFromArrivalWindow(visit.arrivalWindow);
                    setRescheduleStart(clocks.start);
                    setRescheduleEnd(clocks.end);
                    setShowReschedule(true);
                  }}
                >
                  Reschedule
                </Button>
              ) : null}
            </div>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex items-start justify-between gap-4">
                <dt className="text-slate-500">Date</dt>
                <dd className="text-right font-medium text-slate-900">{shortVisitDate}</dd>
              </div>
              <div className="flex items-start justify-between gap-4">
                <dt className="text-slate-500">Arrival</dt>
                <dd className="text-right font-medium text-slate-900">{arrivalLabel || "Time not set"}</dd>
              </div>
              <div className="flex items-start justify-between gap-4">
                <dt className="text-slate-500">Technician</dt>
                <dd className="text-right font-medium text-slate-900">{technicianLabel || "—"}</dd>
              </div>
            </dl>
            <div className="mt-3">
              <SvrVisitScheduleAlert
                arrivalWindow={visit.arrivalWindow}
                canEdit={!!user && canEditVisitSchedule && !["closed", "waived", "report_sent"].includes(visitStatusKey)}
                showForm={showReschedule}
                rescheduleDate={rescheduleDate}
                rescheduleStart={rescheduleStart}
                rescheduleEnd={rescheduleEnd}
                pending={updateVisit.isPending}
                onOpenForm={() => {
                  setRescheduleDate(toSingaporeDateInputValue(visit.visitDate));
                  const clocks = clockInputsFromArrivalWindow(visit.arrivalWindow);
                  setRescheduleStart(clocks.start);
                  setRescheduleEnd(clocks.end);
                  setShowReschedule(true);
                }}
                onCloseForm={() => setShowReschedule(false)}
                onDateChange={setRescheduleDate}
                onStartChange={setRescheduleStart}
                onEndChange={setRescheduleEnd}
                onInvalid={(message) => toast.error(message)}
                onConfirm={(arrivalWindow) => {
                  updateVisit.mutate({
                    id: visitId,
                    visitDate: parseSingaporeDateInput(rescheduleDate),
                    arrivalWindow,
                  }, {
                    onSuccess: () => {
                      toast.success("Visit rescheduled");
                      setShowReschedule(false);
                      setFocusedActionError(null);
                    },
                  });
                }}
              />
            </div>
          </section>
        </div>

        <section data-testid="svr-scope-of-work" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 text-slate-800">
              <ClipboardList className="h-4 w-4" />
              <h2 className="text-base font-semibold">Scope of work</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-400">Read before starting</span>
              {!editingScopeOfWork && (isAdmin || isSales || isMyPicVisit) && (
                <button
                  type="button"
                  className="text-slate-400 hover:text-slate-700"
                  title="Edit Scope of Work"
                  onClick={() => {
                    setScopeOfWorkText(visit.complaintDescription ?? "");
                    setEditingScopeOfWork(true);
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
          {editingScopeOfWork ? (
            <div className="mt-4 space-y-2">
              <Textarea
                value={scopeOfWorkText}
                onChange={(e) => setScopeOfWorkText(e.target.value)}
                rows={8}
                className="text-sm"
                placeholder="Describe the scope of work, equipment details, areas to be serviced…"
                autoFocus
              />
              {focusedActionError && (
                <p className="text-sm text-red-700" role="alert">{focusedActionError}</p>
              )}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="gap-1.5"
                  disabled={updateVisit.isPending}
                  onClick={() => {
                    updateVisit.mutate(
                      { id: visit.id, complaintDescription: scopeOfWorkText },
                      {
                        onSuccess: () => {
                          setEditingScopeOfWork(false);
                          setFocusedActionError(null);
                          toast.success("Scope of Work updated");
                        },
                      }
                    );
                  }}
                >
                  <Check className="h-3.5 w-3.5" />
                  {updateVisit.isPending ? "Saving…" : "Save"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditingScopeOfWork(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-4 space-y-5">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Original scope</p>
                <p data-testid="svr-scope-body" className="mt-2 whitespace-pre-wrap text-[15px] leading-7 text-slate-800">
                  {visit.complaintDescription || <span className="italic text-slate-400">Not specified</span>}
                </p>
              </div>
              {visit.findingsAndActions?.trim() && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Technician findings</p>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">{visit.findingsAndActions}</p>
                </div>
              )}
              {visit.customerReport?.trim() && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Customer-facing report</p>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">{visit.customerReport}</p>
                </div>
              )}
              {visit.resolutionNotes?.trim() && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Resolution</p>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">{visit.resolutionNotes}</p>
                </div>
              )}
            </div>
          )}
        </section>

        <section data-testid="svr-visit-details" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Visit details</p>
          <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <div className="flex justify-between gap-3 sm:block">
              <dt className="text-slate-500">Appointment</dt>
              <dd className="font-medium text-slate-900 sm:mt-0.5">{visitDateStr}{arrivalLabel ? ` · ${arrivalLabel}` : ""}</dd>
            </div>
            <div className="flex justify-between gap-3 sm:block">
              <dt className="text-slate-500">Assigned technician</dt>
              <dd className="font-medium text-slate-900 sm:mt-0.5">{technicianLabel || "—"}</dd>
            </div>
            <div className="flex justify-between gap-3 sm:block">
              <dt className="text-slate-500">Visit type</dt>
              <dd className="font-medium capitalize text-slate-900 sm:mt-0.5">{visit.visitType === "urgent" ? "Urgent" : "Planned"}</dd>
            </div>
            <div className="flex justify-between gap-3 sm:block">
              <dt className="text-slate-500">PIC</dt>
              <dd className="font-medium text-slate-900 sm:mt-0.5">{visit.picName || "Unassigned"}</dd>
            </div>
            {showFinancials && billing?.proposalRef && (
              <div className="flex justify-between gap-3 sm:block">
                <dt className="text-slate-500">Related proposal</dt>
                <dd className="font-medium text-slate-900 sm:mt-0.5">{billing.proposalRef}</dd>
              </div>
            )}
            <div className="flex justify-between gap-3 sm:block">
              <dt className="text-slate-500">Created</dt>
              <dd className="font-medium text-slate-900 sm:mt-0.5">{visit.createdBy || "—"} · {formatDate(visit.createdAt)}</dd>
            </div>
            <div className="flex justify-between gap-3 sm:block">
              <dt className="text-slate-500">Last updated</dt>
              <dd className="font-medium text-slate-900 sm:mt-0.5">{visit.updatedAt ? formatDateTime(visit.updatedAt as string | Date) : "—"}</dd>
            </div>
          </dl>
          {showFinancials && (
            <div className="mt-4 border-t border-slate-100 pt-3 text-sm">
              <p className="text-slate-500">Billing classification</p>
              <p className="mt-1 font-medium text-slate-900">
                {(visit as any).billingDecision ? (
                  <>
                    {formatSvrBillingDecisionLabel((visit as any).billingDecision)}
                    {(visit as any).billingDecision === "bill" && (visit as any).agreedRate && (
                      <span className="ml-1 font-semibold text-amber-700">
                        — SGD {parseFloat(String((visit as any).agreedRate)).toFixed(2)}
                      </span>
                    )}
                  </>
                ) : visit.billable ? (
                  <span className="inline-flex flex-wrap items-center gap-1 text-amber-700">
                    <DollarSign className="h-3 w-3 shrink-0" />
                    Billable (legacy — no PIC decision recorded)
                    {(visit as any).serviceFee && (
                      <span className="font-medium">— SGD {parseFloat((visit as any).serviceFee).toFixed(2)}</span>
                    )}
                  </span>
                ) : (
                  <span className="text-slate-500">Not billable (legacy)</span>
                )}
              </p>
            </div>
          )}
          {!!billing?.preAgreedChargeEnabled && (
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
              <p className="font-medium">Customer has agreed to pay during visit.</p>
              {billing.invoiceStatus === "paid" || billing.paidAt ? (
                <p className="mt-1 font-semibold text-green-700">Visit Paid</p>
              ) : (
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {billing.paymentLink ? (
                    <a className="underline" href={billing.paymentLink} target="_blank" rel="noopener noreferrer">
                      View Invoice / Payment Link
                    </a>
                  ) : (
                    <span>Invoice/Payment link pending</span>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="mt-4 border-t border-slate-100 pt-3 text-sm">
            <p className="text-slate-500">Customer notification</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {(visit as any).custWhatsappStatus === "sent" && (
                <span className="flex items-center gap-1 text-xs text-green-700">
                  <MessageCircle className="h-3 w-3" />
                  Sent
                  {(visit as any).custWhatsappSentAt ? ` · ${formatDateTime((visit as any).custWhatsappSentAt)}` : ""}
                  {(visit as any).custWhatsappSentBy ? ` · by ${(visit as any).custWhatsappSentBy}` : ""}
                </span>
              )}
              {(visit as any).custWhatsappStatus === "failed" && (
                <span className="flex items-center gap-1 text-xs text-red-600"><XCircle className="h-3 w-3" /> Failed</span>
              )}
              {(visit as any).custWhatsappStatus === "no_phone" && (
                <span className="flex items-center gap-1 text-xs text-gray-400"><MessageCircle className="h-3 w-3" /> No phone on file</span>
              )}
              {([undefined, null, "pending"] as any[]).includes((visit as any).custWhatsappStatus) && (
                <span className="text-xs text-amber-700">Not sent yet</span>
              )}
              {(isAdmin || isSales) && (
                <CustomerWhatsAppButton
                  visitId={visit.id}
                  onSuccess={() => refetchVisit()}
                  alreadySent={(visit as any).custWhatsappStatus === "sent"}
                />
              )}
            </div>
          </div>
        </section>

        {/* ── SERVICE SECTION ── */}
        <div
          id="service"
          className={highlightedStageId === "service" ? "scroll-mt-4 rounded-lg ring-2 ring-violet-300/80 transition-all" : "scroll-mt-4"}
        >
        <SvrStageSection
          label="Service"
          active={["scheduled"].includes(visitStatusKey)}
          past={["pending_review", "report_sent", "awaiting_payment", "closed", "waived"].includes(visitStatusKey)}
          open={expandedStageId === "service"}
          onRequestExpand={() => setExpandedStageId("service")}
          statusSubline={serviceStatusSub}
        >
        {/* Customer-facing public SVR link (staff/admin only) */}
        {canManageCustomerSvrLink && (
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs font-medium text-muted-foreground mb-1.5">Customer SVR Link</p>
              {customerSvrLink ? (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <code className="text-xs bg-gray-100 rounded px-2 py-1.5 flex-1 truncate text-gray-600">
                    {customerSvrLink}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0 text-xs gap-1.5"
                    onClick={async () => {
                      await navigator.clipboard.writeText(customerSvrLink);
                      toast.success("Customer SVR link copied");
                    }}
                  >
                    <Copy className="h-3.5 w-3.5" />
                    Copy Link
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0 text-xs"
                    onClick={() => {
                      window.open(customerSvrLink, "_blank", "noopener,noreferrer");
                    }}
                  >
                    Open Customer View
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  No public tracking token is currently available for this SVR.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Customer Info */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Customer
              </span>
              {isAdmin && (
                <button
                  onClick={() => setEditingCustomer(true)}
                  className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50"
                >
                  Edit
                </button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <p className="font-medium">
              <button
                className="hover:underline text-left"
                onClick={() => customer && setLocation(`/customers/id/${customer.id}`)}
              >
                {customer?.name ?? "Unknown"}
              </button>
            </p>
            {fullAddress && (
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3 w-3 flex-shrink-0" />
                {fullAddress}
              </p>
            )}
            {customer?.contact && (
              <p className="text-sm text-muted-foreground">{customer.contact}</p>
            )}
          </CardContent>
        </Card>

        {/* Edit Customer Form */}
        {editingCustomer && (
          <Card id="svr-edit-customer" className="border-blue-200 bg-blue-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Edit Customer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="editName">Full Name</Label>
                  <Input
                    id="editName"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="editContact">Contact Number</Label>
                  <Input
                    id="editContact"
                    value={editContact}
                    onChange={(e) => setEditContact(e.target.value)}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="editEmail">Email</Label>
                  <Input
                    id="editEmail"
                    type="email"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="editUnitNumber">Unit Number</Label>
                  <Input
                    id="editUnitNumber"
                    value={editUnitNumber}
                    onChange={(e) => setEditUnitNumber(e.target.value)}
                    placeholder="e.g. #12-34"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="editPostalCode">Postal Code</Label>
                  <Input
                    id="editPostalCode"
                    value={editPostalCode}
                    onChange={(e) => setEditPostalCode(e.target.value)}
                    placeholder="6-digit postal code"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="editAddress">Street Address</Label>
                  <Input
                    id="editAddress"
                    value={editAddress}
                    onChange={(e) => setEditAddress(e.target.value)}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="editSalesperson">Account Manager (Salesperson)</Label>
                  <select
                    id="editSalesperson"
                    value={editSalespersonId ?? ""}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setEditSalespersonId(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full mt-1.5 border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">— Unassigned —</option>
                    {salesStaff?.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditingCustomer(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    const resolvedName =
                      editName.trim() || (customer?.name ? String(customer.name).trim() : "") || undefined;
                    updateCustomer.mutate({
                      id: customer?.id ?? 0,
                      name: resolvedName,
                      contact: editContact || undefined,
                      email: editEmail || undefined,
                      address: editAddress,
                      unitNumber: editUnitNumber || undefined,
                      postalCode: editPostalCode || undefined,
                      salespersonId: editSalespersonId,
                    });
                  }}
                  disabled={updateCustomer.isPending}
                  className="bg-black text-white hover:bg-gray-800"
                >
                  {updateCustomer.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Multi-Tech Assignees Card */}
        {(isAdmin || visitAssignees !== undefined) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Assigned Staff
                </span>
                {isAdmin ? (
                  <button
                    onClick={() => {
                      setPendingAssignees(visitAssignees?.map(a => ({ userId: a.userId, userName: a.userName, role: a.role })) ?? []);
                      setShowAssigneePanel(true);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50"
                  >
                    {visitAssignees?.length ? "Edit" : "+ Assign"}
                  </button>
                ) : null}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {visitAssignees && visitAssignees.length > 0 ? (
                <div className="space-y-1.5">
                  {visitAssignees.map((a) => (
                    <div key={a.id} className="flex items-center gap-2 text-sm">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        a.role === "technician" ? "bg-blue-500" : a.role === "programmer" ? "bg-violet-500" : "bg-emerald-500"
                      }`} />
                      <span className="font-medium">{a.userName}</span>
                      <span className="text-xs text-muted-foreground capitalize">({a.role})</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No additional staff assigned. Only the primary technician is assigned.</p>
              )}

              {Boolean((visit as any).inventoryTeamRequested) && !showAssigneePanel && (
                <div className="mt-3 pt-3 border-t space-y-1.5">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Inventory Team (legacy)
                    <span className="ml-1.5 text-[10px] font-normal bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                      Staff only — use Bring list for new requests
                    </span>
                  </p>
                  <p className="text-sm whitespace-pre-wrap">
                    {String((visit as any).inventoryTeamMessage ?? "").trim() || "(no message)"}
                  </p>
                </div>
              )}

              {/* Assignee editor panel */}
              {showAssigneePanel && (
                <div className="mt-3 pt-3 border-t space-y-3">
                  <p className="text-xs font-medium text-muted-foreground">Select staff to assign to this visit:</p>
                  {/* Current pending list */}
                  {pendingAssignees.length > 0 && (
                    <div className="space-y-1">
                      {pendingAssignees.map((a, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm bg-gray-50 rounded px-2 py-1">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            a.role === "technician" ? "bg-blue-500" : a.role === "programmer" ? "bg-violet-500" : "bg-emerald-500"
                          }`} />
                          <span className="flex-1">{a.userName}</span>
                          <span className="text-xs text-muted-foreground capitalize">{a.role}</span>
                          <button
                            onClick={() => setPendingAssignees(prev => prev.filter((_, i) => i !== idx))}
                            className="text-red-500 hover:text-red-700 text-xs px-1"
                          >✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Add new assignee row */}
                  <AddAssigneeRow
                    allUsers={allUsers ?? []}
                    existingIds={pendingAssignees.map(a => a.userId)}
                    onAdd={(u) => setPendingAssignees(prev => [...prev, u])}
                  />
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => setShowAssigneePanel(false)}>Cancel</Button>
                    <Button
                      size="sm"
                      className="flex-1 bg-black text-white hover:bg-gray-800"
                      disabled={setAssignees.isPending}
                      onClick={() => {
                        setAssignees.mutate({ visitId, assignees: pendingAssignees });
                      }}
                    >
                      {setAssignees.isPending ? "Saving…" : "Save Assignees"}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {(isAdmin || user?.role === "finance" || user?.role === "sales") && visitId > 0 && (
          <SvrStaffDeliveryPanel visitId={visitId} canRetry={isAdmin || user?.role === "finance"} />
        )}

        {visitId > 0 ? <SvrAdditionalItemsCard visitId={visitId} role={user?.role} visitClosed={["closed", "waived"].includes(visitStatusKey)} /> : null}

        {/* Visit Progress — shown in left rail; no duplicate card in right column */}

        {/* Visit update — field staff only on assigned visits; PIC/admin broader access */}
        {canEditVisitUpdate &&
          ["scheduled", "pending_review", "awaiting_payment", "waived", "report_sent", "closed"].includes(visitStatusKey) && (
          <Card data-testid="svr-work-performed">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Work performed
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-slate-500">
                Internal notes stay on the staff record. The customer-facing report is written in PIC review and is the only text sent to the customer.
              </p>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">
                  Internal notes
                  <span className="ml-1.5 text-xs font-normal bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Staff only — not shared with customer</span>
                </label>
                <Textarea
                  id="svr-internal-notes"
                  value={internalNotes}
                  onChange={(e) => {
                    setInternalNotes(e.target.value);
                    setInternalNotesDirty(true);
                    setFocusedActionError(null);
                  }}
                  placeholder="Work performed, findings, equipment, parts, and further action. Staff only."
                  rows={5}
                />
              </div>

              {/* Parts Cost removed from SVR — only applicable to RO */}

              {visitStatusKey === "scheduled" && visit.cleoFieldSessionActive && (
                <p className="text-xs text-muted-foreground">
                  Session in progress: the technician is logging on-site via Cleo. Internal notes will refresh while this
                  is active. &quot;Save and proceed&quot; is available when the field session ends.
                </p>
              )}

              {focusedActionError && (
                <p className="text-sm text-red-700" role="alert">{focusedActionError}</p>
              )}

              <Button
                onClick={submitVisitUpdate}
                disabled={
                  submitUpdate.isPending ||
                  (visitStatusKey === "scheduled" && visit.cleoFieldSessionActive) ||
                  (!internalNotes.trim() && !customerReport.trim())
                }
                style={
                  visitStatusKey === "scheduled" && !visit.cleoFieldSessionActive
                    ? { background: "#f59e0b", color: "white", width: "100%", fontWeight: 700, fontSize: 14, letterSpacing: "0.04em", padding: "10px 0" }
                    : { width: "100%" }
                }
                className={
                  visitStatusKey === "scheduled" && visit.cleoFieldSessionActive
                    ? "w-full pointer-events-none opacity-60 cursor-not-allowed text-muted-foreground"
                    : visitStatusKey === "scheduled" && !visit.cleoFieldSessionActive
                      ? "w-full"
                      : "w-full bg-black text-white hover:bg-gray-800"
                }
                variant={visitStatusKey === "scheduled" && visit.cleoFieldSessionActive ? "outline" : "default"}
              >
                {submitUpdate.isPending ? "Saving..."
                  : visitStatusKey === "scheduled" && visit.cleoFieldSessionActive
                    ? "SESSION IN PROGRESS"
                  : visitStatusKey === "scheduled"
                    ? "SAVE AND PROCEED →"
                  : ["report_sent", "awaiting_payment", "closed"].includes(visitStatusKey)
                    ? "Save Report Edit"
                  : "Save Update"}
              </Button>
            </CardContent>
          </Card>
        )}
        {/* Technician's Update read-only card removed — Visit Update card below is the single source of truth */}
        </SvrStageSection>{/* end SERVICE section */}
        </div>

        {/* Visit-level media must remain visible even when a completed Service stage is collapsed. */}
        <Card data-testid="svr-field-media">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Photos and files{svrMedia && svrMedia.length > 0 ? ` (${svrMedia.length})` : ""}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {svrMedia && svrMedia.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {svrMedia.map((item) => (
                  <div key={item.id} className="group relative rounded-md overflow-hidden border bg-gray-50">
                    {item.mimeType?.startsWith("video/") ? (
                      <video
                        src={item.s3Url}
                        controls
                        className="w-full aspect-video object-cover"
                        preload="metadata"
                      />
                    ) : item.mimeType?.startsWith("image/") || !item.mimeType ? (
                      <a href={item.s3Url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={item.s3Url}
                          alt={item.caption ?? "Field photo"}
                          className="w-full aspect-video object-cover hover:opacity-90 transition-opacity"
                        />
                      </a>
                    ) : (
                      <a href={item.s3Url} target="_blank" rel="noopener noreferrer" className="flex aspect-video items-center justify-center px-3 text-sm text-slate-700 underline">
                        Open file
                      </a>
                    )}
                    <div className="space-y-1 px-2 py-1.5">
                      {item.caption && (
                        <p className="text-xs text-muted-foreground leading-snug">{item.caption}</p>
                      )}
                      <p className="text-[11px] text-slate-400">{formatDateTime(item.createdAt)}</p>
                    </div>
                    <a
                      href={item.s3Url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="absolute top-1.5 right-1.5 bg-black/50 text-white rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Download"
                    >
                      <Download className="h-3 w-3" />
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">No photos or files on this visit yet.</p>
            )}
          </CardContent>
        </Card>

        {/* ── BLOCKER BANNER: Service → PIC ── */}
        {visitStatusKey === "scheduled" && (
          <div className="flex items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3">
            <svg className="h-4 w-4 shrink-0 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <p className="text-xs font-bold uppercase tracking-wide text-amber-800">Waiting for Site Update by Technician</p>
          </div>
        )}

        {/* ── PIC SECTION ── */}
        <div
          id="pic"
          className={highlightedStageId === "pic" ? "scroll-mt-4 rounded-lg ring-2 ring-violet-300/80 transition-all" : "scroll-mt-4"}
        >
        <SvrStageSection
          label="PIC"
          active={["pending_review"].includes(visitStatusKey)}
          past={["report_sent", "awaiting_payment", "closed", "waived"].includes(visitStatusKey)}
          open={expandedStageId === "pic"}
          onRequestExpand={() => setExpandedStageId("pic")}
          statusSubline={picStatusSub}
        >
        {/* PIC Review Section */}
        {isSales && (isMyPicVisit || !visit.picId) && visitStatusKey === "pending_review" && (
          <Card className="border-amber-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2 text-amber-800">
                <AlertTriangle className="h-4 w-4" />
                PIC Review Required
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Show technician's internal note for PIC context */}
              {visit.internalNotes && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                    Technician's Internal Note
                    <span className="text-xs font-normal bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Staff only</span>
                  </p>
                  <p className="text-sm whitespace-pre-wrap bg-amber-50 rounded-md p-3 border border-amber-100">{visit.internalNotes}</p>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-muted-foreground">Customer-Facing Report</label>
                  <button
                    onClick={() => polishReport.mutate({ rawNotes: internalNotes || (visit.internalNotes ?? visit.findingsAndActions ?? ""), visitId: visit.id })}
                    disabled={polishReport.isPending || (!(internalNotes || visit.internalNotes || visit.findingsAndActions))}
                    className="flex items-center gap-1 text-xs text-purple-700 hover:text-purple-900 disabled:opacity-40"
                  >
                    {polishReport.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                    Polish with AI
                  </button>
                </div>
                <Textarea
                  value={customerReport}
                  onChange={(e) => setCustomerReport(e.target.value)}
                  placeholder="Edit the customer-facing report before sending..."
                  rows={4}
                />
              </div>
              {/* Billing decision summary (if already set) */}
              {(visit as any).billingDecision && (
                <div className="text-xs bg-gray-50 border border-gray-200 rounded px-3 py-2 space-y-0.5">
                  <span className="font-medium text-muted-foreground">Billing classification: </span>
                  <span className="font-semibold">
                    {formatSvrBillingDecisionLabel((visit as any).billingDecision)}
                    {(visit as any).billingDecision === "bill" && (visit as any).agreedRate
                      ? ` — SGD ${parseFloat(String((visit as any).agreedRate)).toFixed(2)}`
                      : ""}
                  </span>
                  {(visit as any).billingNote && <p className="text-muted-foreground">{(visit as any).billingNote}</p>}
                </div>
              )}
              {/* Soft lock notice */}
              {(visit as any).billingDecision && visit.billing && visit.billing.invoiceStatus !== "pending" && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  Billing decision locked — invoice is already in progress. Contact admin to amend.
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => { setPendingAction("close"); setShowBillingDialog(true); }}
                  disabled={picAction.isPending || (!customerReport.trim() && !visit.findingsAndActions?.trim())}
                  className="flex-1"
                >
                  Close Without Sending
                </Button>
                <Button
                  onClick={() => { setPendingAction("send_report"); setShowBillingDialog(true); }}
                  disabled={picAction.isPending || !customerReport.trim()}
                  style={{ background: "#7c3aed", color: "white", fontWeight: 700, letterSpacing: "0.04em" }}
                  className="flex-1"
                >
                  {picAction.isPending ? "Sending..." : "SEND REPORT AND PROCEED →"}
                </Button>
              </div>
              {/* Billing Decision Dialog */}
              {showBillingDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                  <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6 space-y-4">
                    <h3 className="text-base font-semibold">Billing Decision Required</h3>
                    <p className="text-sm text-muted-foreground">Before closing this visit, please declare the billing outcome.</p>
                    <div className="space-y-2">
                      {[
                        { value: "bill" as const, label: "Billable" },
                        { value: "no_charge_warranty_goodwill" as const, label: "Not Billable (Goodwill)" },
                        { value: "no_charge_contract" as const, label: "Not Billable (Within Warranty)" },
                        {
                          value: "not_billable_new_job_within_1_year" as const,
                          label: "Not Billable (New Job – Within 1 Year)",
                        },
                      ].map((opt) => (
                        <label key={opt.value} className={`flex items-center gap-3 border rounded-md px-3 py-2.5 cursor-pointer transition-colors ${billingDecision === opt.value ? "border-black bg-gray-50" : "border-gray-200 hover:border-gray-400"}`}>
                          <input
                            type="radio"
                            name="billingDecision"
                            value={opt.value}
                            checked={billingDecision === opt.value}
                            onChange={() => setBillingDecision(opt.value as any)}
                            className="accent-black"
                          />
                          <span className="text-sm font-medium">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    {billingDecision === "bill" && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground block mb-1">Agreed Rate (SGD) *</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={agreedRate}
                            onChange={(e) => setAgreedRate(e.target.value)}
                            placeholder="e.g. 120.00"
                            className="w-full border border-gray-200 pl-7 pr-3 py-2 text-sm focus:outline-none focus:border-black transition-colors rounded"
                          />
                        </div>
                      </div>
                    )}
                    {billingDecision && billingDecision !== "bill" && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground block mb-1">Reason / Note (optional)</label>
                        <textarea
                          value={billingNote}
                          onChange={(e) => setBillingNote(e.target.value)}
                          placeholder="Brief reason for no charge..."
                          rows={2}
                          className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors rounded resize-none"
                        />
                        <p className="text-xs text-amber-700 mt-1">Admin will be notified of this no-charge decision.</p>
                      </div>
                    )}
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        onClick={() => { setShowBillingDialog(false); setPendingAction(null); setBillingDecision(""); setAgreedRate(""); setBillingNote(""); }}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => {
                          if (!billingDecision) { return; }
                          if (billingDecision === "bill" && !agreedRate) { return; }
                          picAction.mutate({
                            id: visitId,
                            action: pendingAction!,
                            customerReport: customerReport.trim() ? customerReport : undefined,
                            billingDecision: billingDecision as any,
                            agreedRate: billingDecision === "bill" ? parseFloat(agreedRate) : undefined,
                            billingNote: billingNote || undefined,
                          });
                          setShowBillingDialog(false);
                          setPendingAction(null);
                          setBillingDecision("");
                          setAgreedRate("");
                          setBillingNote("");
                        }}
                        disabled={
                          !billingDecision ||
                          (billingDecision === "bill" && !agreedRate) ||
                          picAction.isPending ||
                          (!customerReport.trim() && !visit.findingsAndActions?.trim())
                        }
                        style={(
                          (billingDecision && billingDecision !== "bill") ||
                          (pendingAction === "close")
                        ) ? { background: "#111827", color: "white", fontWeight: 700, letterSpacing: "0.04em" } : {}}
                        className="flex-1 bg-black text-white hover:bg-gray-800"
                      >
                        {picAction.isPending ? "Saving..."
                          : (billingDecision && billingDecision !== "bill")
                            ? "CLOSE THIS CASE"
                            : pendingAction === "close"
                              ? "CLOSE THIS CASE"
                              : "Send Report"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Customer Report (sent) */}
        {visit.customerReport && ["closed", "report_sent", "waived"].includes(visitStatusKey) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Customer Report
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm whitespace-pre-wrap bg-gray-50 rounded-md p-3 border border-gray-200">{visit.customerReport}</p>
              {visit.reportSentAt ? (
                <p className="text-xs text-green-700 flex items-center gap-1">
                  <span>✓</span>
                  Successfully sent to customer via {(visit as any).reportSentVia === "whatsapp" ? "WhatsApp" : (visit as any).reportSentVia === "email" ? "Email" : "unknown"} on{" "}
                  {new Date(visit.reportSentAt).toLocaleDateString("en-SG", { day: "numeric", month: "long", year: "numeric" })}{" "}
                  {new Date(visit.reportSentAt).toLocaleTimeString("en-SG", { hour: "2-digit", minute: "2-digit", hour12: true })}{" "}
                  by {visit.reportSentBy || "—"}
                </p>
              ) : null}
              {/* Send Report buttons — PIC or admin only */}
              {isSales && (
                <div className="pt-1 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">
                    {visit.reportSentAt ? "Re-send report to customer:" : "Send report to customer:"}
                  </p>
                  {sendConfirm ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-2">
                      <p className="text-xs text-amber-800">
                        {visit.reportSentAt
                          ? `This report was already sent. Send again via ${sendConfirm === "whatsapp" ? "WhatsApp" : "email"}?`
                          : `Send this report to ${visit.customer?.name ?? "the customer"} via ${sendConfirm === "whatsapp" ? "WhatsApp" : "email"}?`}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSendConfirm(null)}
                          className="flex-1"
                          disabled={sendReport.isPending}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => sendReport.mutate({ id: visitId, channel: sendConfirm })}
                          disabled={sendReport.isPending}
                          className="flex-1 bg-black text-white hover:bg-gray-800"
                        >
                          {sendReport.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Confirm Send"}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSendConfirm("whatsapp")}
                        disabled={!visit.customer?.contact}
                        title={!visit.customer?.contact ? "Customer has no phone number on record" : undefined}
                        className="flex-1 gap-1.5"
                      >
                        <MessageSquare className="h-3.5 w-3.5" />
                        {visit.reportSentAt ? "Re-send via WhatsApp" : "Send via WhatsApp"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSendConfirm("email")}
                        disabled={!visit.customer?.email}
                        title={!visit.customer?.email ? "Customer has no email address on record" : undefined}
                        className="flex-1 gap-1.5"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        {visit.reportSentAt ? "Re-send via Email" : "Send via Email"}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        </SvrStageSection>{/* end PIC section */}
        </div>

        {/* ── BLOCKER BANNER: PIC → Finance ── */}
        {visitStatusKey === "pending_review" && (
          <div className="flex items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3">
            <svg className="h-4 w-4 shrink-0 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <p className="text-xs font-bold uppercase tracking-wide text-amber-800">Waiting for PIC Review</p>
          </div>
        )}

        {/* ── FINANCE SECTION ── */}
        <div
          id="finance"
          ref={financeSectionRef}
          className={highlightedStageId === "finance" ? "scroll-mt-4 rounded-lg ring-2 ring-violet-300/80 transition-all" : "scroll-mt-4"}
        >
        <SvrStageSection
          label="Finance"
          active={["report_sent", "awaiting_payment"].includes(visitStatusKey)}
          past={["closed", "waived"].includes(visitStatusKey)}
          open={expandedStageId === "finance"}
          onRequestExpand={() => setExpandedStageId("finance")}
          statusSubline={financeStatusSub}
        >
        {/* Billing pending — auto-closed on technician done; Finance / PIC sets billing here */}
        {isBillingPending && (
          <Card className="border-amber-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2 text-amber-800">
                <AlertTriangle className="h-4 w-4" />
                Billing decision pending
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                This visit was closed automatically when the technician finished. No invoice has been raised and nothing has been waived yet.
                {visit.reportSentAt ? "" : " The report has not been sent to the customer."}
              </p>
              {canSetPendingBilling ? (
                <div className="space-y-2">
                  {[
                    { value: "bill" as const, label: "Billable" },
                    { value: "no_charge_warranty_goodwill" as const, label: "Not Billable (Goodwill)" },
                    { value: "no_charge_contract" as const, label: "Not Billable (Within Warranty)" },
                    { value: "not_billable_new_job_within_1_year" as const, label: "Not Billable (New Job – Within 1 Year)" },
                  ].map((opt) => (
                    <label key={opt.value} className={`flex items-center gap-3 border rounded-md px-3 py-2 cursor-pointer transition-colors ${billingDecision === opt.value ? "border-black bg-gray-50" : "border-gray-200 hover:border-gray-400"}`}>
                      <input
                        type="radio"
                        name="pendingBillingDecision"
                        value={opt.value}
                        checked={billingDecision === opt.value}
                        onChange={() => setBillingDecision(opt.value)}
                        className="accent-black"
                      />
                      <span className="text-sm font-medium">{opt.label}</span>
                    </label>
                  ))}
                  {billingDecision === "bill" && (
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={agreedRate}
                      onChange={(e) => setAgreedRate(e.target.value)}
                      placeholder="Agreed rate (SGD), e.g. 120.00"
                      className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black rounded"
                    />
                  )}
                  {billingDecision && billingDecision !== "bill" && (
                    <textarea
                      value={billingNote}
                      onChange={(e) => setBillingNote(e.target.value)}
                      placeholder="Reason / note (optional)"
                      rows={2}
                      className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black rounded resize-none"
                    />
                  )}
                  <Button
                    size="sm"
                    className="w-full bg-black text-white hover:bg-gray-800"
                    disabled={
                      !billingDecision ||
                      (billingDecision === "bill" && !(parseFloat(agreedRate) > 0)) ||
                      setPendingBilling.isPending
                    }
                    onClick={() => {
                      if (!billingDecision) return;
                      setPendingBilling.mutate({
                        id: visitId,
                        billingDecision,
                        agreedRate: billingDecision === "bill" ? parseFloat(agreedRate) : undefined,
                        billingNote: billingNote || undefined,
                      });
                    }}
                  >
                    {setPendingBilling.isPending ? "Saving..." : "Save billing decision"}
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Finance or the PIC will set billing for this visit.</p>
              )}
            </CardContent>
          </Card>
        )}
        {/* No Charge info card — shown when SVR is non-billable */}
        {!isBillingPending && !visit.billable && (billing?.invoiceStatus === "waived" || ["closed", "waived"].includes(visitStatusKey)) && (
          <Card>
            <CardContent className="py-4">
              <div className="flex items-start gap-3 p-3 rounded-md bg-gray-50 border border-gray-200">
                <div className="mt-0.5 h-5 w-5 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                  <svg className="h-3 w-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">No Charge</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {(visit as any).billingDecision
                      ? `${formatSvrBillingDecisionLabel((visit as any).billingDecision)} — no invoice required.`
                      : "This visit was closed without billing."}
                  </p>
                  {(visit as any).billingNote && (
                    <p className="text-xs text-gray-500 mt-1">Note: {(visit as any).billingNote}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {/* Billing Section (Finance/Admin) */}
        {visit.billable && isFinance && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Billing
                {billing?.invoiceStatus && (
                  <span className={`ml-auto text-xs font-normal px-2 py-0.5 rounded-full ${
                    billing.invoiceStatus === "paid" ? "bg-green-100 text-green-800" :
                    billing.invoiceStatus === "raised" ? "bg-blue-100 text-blue-800" :
                    billing.invoiceStatus === "outstanding" ? "bg-red-100 text-red-800" :
                    "bg-amber-100 text-amber-800"
                  }`}>
                    {billing.invoiceStatus === "paid" ? "Paid" :
                     billing.invoiceStatus === "raised" ? "Invoice Raised" :
                     billing.invoiceStatus === "outstanding" ? "Outstanding" :
                     "Pending"}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Waiver status */}
              {Boolean(visit.waiverRequested) && (
                <div className={`p-3 rounded-md text-sm ${
                  visit.waiverApproved === true
                    ? "bg-purple-50 border border-purple-200 text-purple-800"
                    : visit.waiverApproved === false
                    ? "bg-red-50 border border-red-200 text-red-800"
                    : "bg-amber-50 border border-amber-200 text-amber-800"
                }`}>
                  <p className="font-medium">
                    {visit.waiverApproved === true ? "Waiver Approved" :
                     visit.waiverApproved === false ? "Waiver Rejected" :
                     "Waiver Requested"}
                  </p>
                  {visit.waiverReason && <p className="text-xs mt-0.5">Reason: {visit.waiverReason}</p>}
                  {visit.waiverRequestedBy && <p className="text-xs mt-0.5">By: {visit.waiverRequestedBy}</p>}
                  {isAdmin && visit.waiverApproved === null && (
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" onClick={() => decideWaiver.mutate({ id: visitId, approved: false })} disabled={decideWaiver.isPending} className="text-red-700 border-red-200 hover:bg-red-50">Reject</Button>
                      <Button size="sm" onClick={() => decideWaiver.mutate({ id: visitId, approved: true })} disabled={decideWaiver.isPending} className="bg-purple-700 text-white hover:bg-purple-800">Approve Waiver</Button>
                    </div>
                  )}
                </div>
              )}

              {/* Step 1 — Invoice Raised (summary only) */}
              {billing?.invoiceUrl && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground font-medium">Step 1 — Invoice Raised</p>
                  <div className="bg-gray-50 rounded-md p-3 space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Invoice status</span>
                      <span className="font-medium capitalize">{billing.invoiceStatus ?? "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Exact amount</span>
                      <span className="font-semibold">
                        {billing.rate != null || (visit as any).agreedRate
                          ? formatSvrBillingMoneySgd(billing.rate ?? (visit as any).agreedRate)
                          : "—"}
                      </span>
                    </div>
                    {billing.invoiceNumber ? (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Invoice number</span>
                        <span className="font-medium">{billing.invoiceNumber}</span>
                      </div>
                    ) : null}
                    {billing.raisedAt ? (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Raised date</span>
                        <span>{formatDate(billing.raisedAt)}</span>
                      </div>
                    ) : null}
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">PIC notification</span>
                      {(billing as any).confirmedAt ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-800">
                          PIC Informed
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Pending confirmation</span>
                      )}
                    </div>
                    {billing.paidAt ? (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Paid</span>
                        <span className="text-green-700 font-medium">{formatDate(billing.paidAt)}</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}

              {/* Step 1: Generate Invoice (Finance only, before invoice exists) */}
              {isFinance && !visit.waiverApproved && billing?.invoiceStatus === "pending" && (
                <div className="space-y-3 pt-1">
                  <p className="text-xs text-muted-foreground font-medium">Step 1 — Generate Invoice</p>

                  {/* Editable amount — pre-filled from billing rate, Finance/Admin can override */}
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">
                      Invoice Amount (SGD, incl. GST)
                      {billing?.rate != null && Number(billing.rate) !== 0 && (
                        <span className="ml-1.5 text-gray-400 font-normal">— suggested: {formatSvrBillingMoneySgd(billing.rate)}</span>
                      )}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={invoiceAmount}
                      onChange={(e) => setInvoiceAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black"
                    />
                  </div>

                  {/* Editable description — pre-filled from Final Report, Finance/Admin can edit */}
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">
                      Invoice Description
                      <span className="ml-1.5 text-gray-400 font-normal">— auto-filled from Final Report, editable</span>
                    </label>
                    <textarea
                      value={invoiceDescription}
                      onChange={(e) => setInvoiceDescription(e.target.value)}
                      rows={4}
                      placeholder="No Final Report yet — complete the Visit Update above first, or type a description here."
                      className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black resize-none"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Payment Link <span className="text-gray-400">(optional — e.g. CardUp or online card)</span></label>
                    <input
                      type="url"
                      value={paymentLink}
                      onChange={(e) => setPaymentLink(e.target.value)}
                      placeholder="https://..."
                      className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">
                      Sales Portal Proposal Ref
                      <span className="ml-1.5 text-gray-400 font-normal">— links payment to salesperson dashboard (e.g. PROP-2026-0042)</span>
                    </label>
                    <input
                      type="text"
                      value={proposalRef}
                      onChange={(e) => setProposalRef(e.target.value.toUpperCase())}
                      placeholder="PROP-2026-XXXX"
                      className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black font-mono"
                    />
                  </div>
                  <Button
                    className="w-full bg-black text-white hover:bg-gray-800"
                    onClick={() => generateInvoice.mutate({
                      visitId,
                      description: invoiceDescription || undefined,
                      amount: invoiceAmount ? parseFloat(invoiceAmount) : undefined,
                      proposalRef: proposalRef.trim() || undefined,
                    })}
                    disabled={generateInvoice.isPending}
                  >
                    {generateInvoice.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</> : <><FileText className="h-4 w-4 mr-2" />Generate Invoice PDF</>}
                  </Button>
                </div>
              )}

              {/* Step 2: Review & Confirm — before customer delivery */}
              {isFinance && billing?.invoiceUrl && billing.invoiceStatus === "raised" && (!(billing as any).confirmedAt || svrInvoiceReviseOpen) && (
                <div className="space-y-3 pt-1">
                  <p className="text-xs text-muted-foreground font-medium">Step 2 — Review &amp; Confirm</p>
                  {["queued", "sending", "submitted", "sent", "delivered", "read", "awaiting_response"].includes(String((billing as any).invoiceWhatsappSendStatus ?? "")) ? (
                    <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                      A previous invoice was already sent to the customer. Revising does not change that historical send. The revised invoice must be confirmed and sent deliberately. It will not be sent automatically.
                    </p>
                  ) : null}
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">
                      Invoice Amount (SGD, incl. GST)
                      <span className="ml-1.5 text-gray-400 font-normal">— edit to override before regenerating</span>
                    </label>
                    <div className="flex flex-wrap items-end gap-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={invoiceAmount || String(billing.rate ?? "")}
                        onChange={(e) => setInvoiceAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full max-w-[200px] text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black"
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="shrink-0"
                        disabled={generateInvoice.isPending}
                        onClick={() => {
                          const raw = (invoiceAmount || String(billing.rate ?? "")).trim();
                          const amt = parseFloat(raw);
                          if (!Number.isFinite(amt) || amt < 0) {
                            toast.error("Enter a valid amount");
                            return;
                          }
                          if (amt === 0) {
                            setWaiveConfirmOpen(true);
                            return;
                          }
                          const alreadySent = ["queued", "sending", "submitted", "sent", "delivered", "read", "awaiting_response"].includes(
                            String((billing as any).invoiceWhatsappSendStatus ?? "")
                          );
                          if (alreadySent && !svrReviseSentWarningOpen) {
                            setSvrReviseSentWarningOpen(true);
                            toast.error("This invoice was already sent. Confirm revision below — historical send records are preserved and the revised invoice will not be sent automatically.");
                            return;
                          }
                          generateInvoice.mutate({
                            visitId,
                            description: invoiceDescription || billing.invoiceDescription || undefined,
                            amount: amt,
                            logRevision: true,
                          });
                        }}
                      >
                        Revise
                      </Button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">
                      Invoice Description
                      <span className="ml-1.5 text-gray-400 font-normal">— from Final Report, editable</span>
                    </label>
                    <textarea
                      value={invoiceDescription || billing.invoiceDescription || ""}
                      onChange={(e) => setInvoiceDescription(e.target.value)}
                      rows={3}
                      className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black resize-none"
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="h-4 w-4 text-blue-600" />
                      <span className="font-medium text-blue-900">{billing.invoiceNumber ?? "Invoice PDF"}</span>
                    </div>
                    <a href={invoicePdfPath ?? billing.invoiceUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 underline">
                      <Download className="h-3.5 w-3.5" />Open PDF
                    </a>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Payment Link <span className="text-gray-400">(CardUp / other)</span></label>
                    <input
                      type="url"
                      value={paymentLink}
                      onChange={(e) => setPaymentLink(e.target.value)}
                      placeholder="https://..."
                      className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() =>
                        generateInvoice.mutate({
                          visitId,
                          description: invoiceDescription || billing.invoiceDescription || undefined,
                          amount: invoiceAmount ? parseFloat(invoiceAmount) : undefined,
                        })
                      }
                      disabled={generateInvoice.isPending}
                    >
                      Regenerate
                    </Button>
                    <Button
                      className="flex-1 bg-black text-white hover:bg-gray-800"
                      onClick={() => confirmInvoice.mutate({ visitId, paymentLink: paymentLink || undefined })}
                      disabled={confirmInvoice.isPending || generateInvoice.isPending}
                    >
                      {confirmInvoice.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "CONFIRM & NOTIFY PIC"}
                    </Button>
                  </div>
                </div>
              )}

              {isFinance && billing?.invoiceUrl && (billing as any).confirmedAt && !svrInvoiceReviseOpen && billing.invoiceStatus !== "paid" && (
                <div className="space-y-3 pt-1">
                  <p className="text-xs text-muted-foreground font-medium">Step 2 — Review &amp; Confirm</p>
                  <div className="bg-green-50 border border-green-200 rounded-md p-3 space-y-1.5 text-sm">
                    <p className="font-medium text-green-900">Invoice confirmed</p>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Invoice amount</span>
                      <span className="font-semibold">{formatSvrBillingMoneySgd(billing.rate)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block">Invoice description</span>
                      <p className="mt-0.5">{billing.invoiceDescription || "—"}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">PDF</span>
                      <a href={invoicePdfPath ?? billing.invoiceUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-blue-600 underline">Open PDF</a>
                    </div>
                    {billing.paymentLink ? (
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground shrink-0">Payment link</span>
                        <span className="truncate text-xs">{billing.paymentLink}</span>
                      </div>
                    ) : (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Payment link</span>
                        <span className="text-xs">Hosted invoice page</span>
                      </div>
                    )}
                    {(billing as any).confirmedBy ? (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Confirmed by</span>
                        <span>{(billing as any).confirmedBy}</span>
                      </div>
                    ) : null}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setSvrInvoiceReviseOpen(true)}>
                    Revise Invoice
                  </Button>
                </div>
              )}

              {/* Step 3: Send to Customer — remains visible after a previous send */}
              {billing?.invoiceUrl && billing.invoiceStatus !== "paid" && !svrInvoiceReviseOpen && (
                <div className="space-y-3 pt-1 border-t">
                  <p className="text-xs text-muted-foreground font-medium">Step 3 — Send to Customer</p>
                  {(() => {
                    const currentFingerprint = buildSvrInvoiceContentFingerprint({
                      billingId: Number((billing as { id?: number }).id ?? 0),
                      rate: billing.rate,
                      invoiceDescription: billing.invoiceDescription,
                      invoiceKey: (billing as { invoiceKey?: string | null }).invoiceKey,
                      invoiceUrl: billing.invoiceUrl,
                      invoiceToken: billing.invoiceToken,
                      paymentLink: billing.paymentLink,
                      raisedAt: billing.raisedAt,
                    });
                    const waEligibility = evaluateSvrInvoiceWhatsAppEligibility({
                      visitStatus: visit.status,
                      invoiceStatus: billing.invoiceStatus,
                      invoiceUrl: billing.invoiceUrl,
                      invoiceToken: billing.invoiceToken,
                      invoiceNumber: billing.invoiceNumber,
                      rate: billing.rate,
                      customerWhatsApp: visit.customer?.contact,
                      whatsappSendStatus: (billing as any).invoiceWhatsappSendStatus,
                      confirmedAt: (billing as any).confirmedAt,
                      confirmedFingerprint: (billing as any).confirmedInvoiceFingerprint,
                      currentFingerprint,
                      lastSuccessfulFingerprint: (billing as any).lastSuccessfulWhatsappFingerprint,
                      isRegenerating: generateInvoice.isPending,
                    });
                    const staffLabel =
                      waEligibility.actionKind === "in_flight"
                        ? waEligibility.staffStatus === "awaiting_response"
                          ? "Awaiting customer response"
                          : waEligibility.staffStatus === "submitted"
                          ? "Submitted"
                          : "Sending"
                        : staffSvrWhatsAppLabel(waEligibility.staffStatus);
                    const showSendButton = Boolean(waEligibility.buttonLabel) && waEligibility.eligible;
                    const confirmMode =
                      waEligibility.actionKind === "resend" ||
                      waEligibility.actionKind === "revised" ||
                      waEligibility.actionKind === "retry"
                        ? waEligibility.actionKind
                        : "initial";
                    return (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-gray-700">{staffLabel}</p>
                        {(billing as any).invoiceWhatsappInitiatedBy || (billing as any).invoiceSentBy || (billing as any).invoiceSentAt ? (
                          <div className="text-xs text-muted-foreground space-y-0.5">
                            {(billing as any).invoiceWhatsappInitiatedBy ? (
                              <p>Initiated by {(billing as any).invoiceWhatsappInitiatedBy}</p>
                            ) : (billing as any).invoiceSentBy ? (
                              <p>Sent by {(billing as any).invoiceSentBy}</p>
                            ) : null}
                            {(billing as any).invoiceSentAt ? (
                              <p>
                                {new Date((billing as any).invoiceSentAt).toLocaleString("en-SG", {
                                  day: "numeric",
                                  month: "long",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                })}
                              </p>
                            ) : null}
                            {visit.customer?.contact ? <p>Recipient {visit.customer.contact}</p> : null}
                          </div>
                        ) : null}
                        {waEligibility.actionKind === "unconfirmed" ? (
                          <p className="text-xs text-amber-800">{waEligibility.unconfirmedMessage}</p>
                        ) : null}
                        {showSendButton && svrWhatsAppConfirmOpen ? (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm space-y-2">
                            {(() => {
                              const confirm = buildSvrInvoiceWhatsAppConfirmation({
                                customerName: visit.customer?.name ?? "Customer",
                                customerWhatsApp: visit.customer?.contact ?? "",
                                invoiceNumber: billing.invoiceNumber ?? "",
                                amount: billing.rate,
                                mode: confirmMode === "retry" ? "initial" : confirmMode,
                                paymentLinkDestination: billing.invoiceToken ? `${window.location.origin}/invoice/${billing.invoiceToken}` : null,
                                lastSentAt: (billing as any).invoiceSentAt,
                                lastSentBy: (billing as any).invoiceSentBy || (billing as any).invoiceWhatsappInitiatedBy,
                                currentAmountDisplay: formatSvrBillingAmountDisplay(billing.rate),
                                picName: visit.picName ?? user?.name ?? "Clarity",
                                picPhone: (user as any)?.contact ?? "+65 6235 9555",
                              });
                              return (
                                <>
                                  <p className="font-medium">{confirm.title}</p>
                                  <ul className="text-xs space-y-1">
                                    <li>Customer: {confirm.customerName}</li>
                                    <li>WhatsApp: {confirm.customerWhatsApp}</li>
                                    <li>Invoice: {confirm.invoiceNumber}</li>
                                    <li>Amount: {confirm.amountDollar}</li>
                                    {confirm.paymentLinkDestination ? (
                                      <li>Payment link: {confirm.paymentLinkDestination}</li>
                                    ) : null}
                                    {confirm.lastSentLabel ? <li>Last sent: {confirm.lastSentLabel}</li> : null}
                                    {confirmMode === "revised" && confirm.currentAmountDisplay ? (
                                      <li>Current confirmed amount: ${confirm.currentAmountDisplay}</li>
                                    ) : null}
                                  </ul>
                                  <p className="text-xs">{confirm.statement}</p>
                                  <div className="rounded-md border border-amber-300 bg-white p-3">
                                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-amber-900">Message customer will receive</p>
                                    <p className="whitespace-pre-wrap text-sm text-gray-900">{confirm.messageBody}</p>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button size="sm" variant="outline" onClick={() => setSvrWhatsAppConfirmOpen(false)} disabled={sendInvoice.isPending}>Cancel</Button>
                                    <Button
                                      size="sm"
                                      className="bg-black text-white hover:bg-gray-800"
                                      disabled={sendInvoice.isPending}
                                      onClick={() =>
                                        sendInvoice.mutate({
                                          visitId,
                                          channel: "whatsapp",
                                          action: confirmMode,
                                          idempotencyKey: svrWhatsAppIdempotencyKeyRef.current || undefined,
                                        })
                                      }
                                    >
                                      {sendInvoice.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : confirm.finalAction}
                                    </Button>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        ) : showSendButton ? (
                          <Button
                            className="w-full bg-black text-white hover:bg-gray-800"
                            disabled={sendInvoice.isPending || generateInvoice.isPending}
                            onClick={() => {
                              svrWhatsAppIdempotencyKeyRef.current = `wa-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
                              setSvrWhatsAppConfirmOpen(true);
                            }}
                          >
                            {waEligibility.buttonLabel}
                          </Button>
                        ) : waEligibility.actionKind === "in_flight" ? (
                          <p className="text-xs text-muted-foreground">A send is currently in progress.</p>
                        ) : null}
                        {(billing as any).invoiceSentAt && ((billing as any).invoiceSentChannel === "email" || Boolean((billing as any).lastSuccessfulWhatsappFingerprint)) && ["raised", "outstanding"].includes(billing.invoiceStatus) ? (
                          svrReminderConfirmOpen ? (
                            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 space-y-3">
                              <div>
                                <p className="font-medium text-blue-950">Send payment reminder via WhatsApp?</p>
                                <p className="text-xs text-blue-800">Payment status will be checked again immediately before sending.</p>
                              </div>
                              <div className="rounded-md border border-blue-200 bg-white p-3">
                                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-blue-900">Message customer will receive</p>
                                <p className="whitespace-pre-wrap text-sm text-gray-900">{renderSvrInvoiceReminderWhatsAppMessage({
                                  customerName: visit.customer?.name ?? "Valued Customer",
                                  invoiceUrl: billing.invoiceToken ? `${window.location.origin}/invoice/${billing.invoiceToken}` : "",
                                })}</p>
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setSvrReminderConfirmOpen(false)} disabled={sendInvoice.isPending}>Cancel</Button>
                                <Button size="sm" className="bg-blue-700 text-white hover:bg-blue-800" disabled={sendInvoice.isPending} onClick={() => {
                                  svrWhatsAppIdempotencyKeyRef.current = `wa-reminder-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
                                  sendInvoice.mutate({ visitId, channel: "whatsapp", action: "reminder", idempotencyKey: svrWhatsAppIdempotencyKeyRef.current });
                                }}>{sendInvoice.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "CONFIRM & SEND REMINDER"}</Button>
                              </div>
                            </div>
                          ) : (
                            <Button size="sm" variant="outline" className="w-full" onClick={() => setSvrReminderConfirmOpen(true)} disabled={sendInvoice.isPending || !visit.customer?.contact}>
                              SEND PAYMENT REMINDER VIA WHATSAPP
                            </Button>
                          )
                        ) : null}
                      </div>
                    );
                  })()}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => scheduleInvoiceSend.mutate({ visitId })}
                      disabled={scheduleInvoiceSend.isPending}
                    >
                      {scheduleInvoiceSend.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Schedule Invoice"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (!billing.paymentLink) return;
                        void navigator.clipboard.writeText(billing.paymentLink);
                        toast.success("Payment link copied");
                      }}
                      disabled={!billing.paymentLink}
                    >
                      Copy Payment Link
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 4: Payment & Close */}
              {canRecordSvrManualPayment(billing) && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-muted-foreground font-medium mb-2">Step 4 — Payment &amp; Close</p>
                  <Button
                    className="w-full bg-green-700 text-white hover:bg-green-800"
                    onClick={() => setConfirmMarkPaidOpen(true)}
                    disabled={markPaid.isPending}
                  >
                    {markPaid.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "CLOSE THIS CASE — Payment Received"}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1 text-center">
                    Records a verified payment with an explicit receipt choice, including historical or prepaid services. Visit closes after payment is saved. Sending the invoice does not record payment.
                  </p>
                </div>
              )}

              {/* Paid confirmation */}
              {billing?.invoiceStatus === "paid" && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800 space-y-2">
                  <div>
                    <p className="font-medium">Payment received</p>
                    {billing.paidAt && (
                      <p className="text-xs mt-0.5">
                        Confirmed {formatDate(billing.paidAt)}
                        {billing.paidBy ? ` by ${billing.paidBy}` : ""}
                      </p>
                    )}
                  </div>
                  {visitStatusKey !== "closed" && visitStatusKey !== "waived" && isFinance && (
                    <Button
                      size="sm"
                      className="w-full bg-green-700 text-white hover:bg-green-800"
                      onClick={() => markPaid.mutate({ visitId })}
                      disabled={markPaid.isPending}
                    >
                      {markPaid.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Complete SVR closure"
                      )}
                    </Button>
                  )}
                </div>
              )}

              {visit.billable &&
                isFinance &&
                parseBillingAuditLog((billing as { billingAuditLog?: string | null }).billingAuditLog).length > 0 && (
                  <div className="pt-2 border-t space-y-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Audit trail</p>
                    <ul className="text-xs text-muted-foreground space-y-1 list-none pl-0">
                      {parseBillingAuditLog((billing as { billingAuditLog?: string | null }).billingAuditLog).map(
                        (e, i) => (
                          <li key={i}>
                            {e.type === "waive"
                              ? `${e.userName} waived the charge — ${formatDateTime(new Date(e.at))}`
                              : `${e.userName} revised the charge to SGD ${Number(e.amount ?? 0).toFixed(2)} — ${formatDateTime(new Date(e.at))}`}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                )}

              {/* Legacy upload option */}
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground mb-2">Override with custom PDF</p>
                <input ref={invoiceInputRef} type="file" accept=".pdf" className="hidden" onChange={handleInvoiceUpload} />
                <Button size="sm" variant="outline" onClick={() => invoiceInputRef.current?.click()} disabled={uploadInvoice.isPending}>
                  <Upload className="h-3.5 w-3.5 mr-1" />
                  {uploadInvoice.isPending ? "Uploading..." : "Upload Custom Invoice PDF"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        </SvrStageSection>{/* end FINANCE section */}
        </div>{/* end financeSectionRef wrapper */}

        {/* ── BLOCKER BANNER: Finance → Close ── */}
        {visitStatusKey === "report_sent" && billing?.invoiceStatus !== "paid" && !(billing as { paidAt?: string | Date | null })?.paidAt && (
          <div className="flex items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3">
            <svg className="h-4 w-4 shrink-0 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <p className="text-xs font-bold uppercase tracking-wide text-amber-800">Waiting for Payment</p>
          </div>
        )}
        {visitStatusKey === "report_sent" &&
          (billing?.invoiceStatus === "paid" || (billing as { paidAt?: string | Date | null })?.paidAt) &&
          isFinance && (
          <div className="flex flex-col gap-2 rounded-md border border-green-300 bg-green-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs font-bold uppercase tracking-wide text-green-800">
              Payment recorded — SVR not closed yet
            </p>
            <Button
              size="sm"
              className="bg-green-700 text-white hover:bg-green-800 shrink-0"
              onClick={() => markPaid.mutate({ visitId })}
              disabled={markPaid.isPending}
            >
              {markPaid.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Complete SVR closure"}
            </Button>
          </div>
        )}
        {visitStatusKey === "awaiting_payment" && (
          <div className="flex items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3">
            <svg className="h-4 w-4 shrink-0 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <p className="text-xs font-bold uppercase tracking-wide text-amber-800">Waiting for Payment Confirmation</p>
          </div>
        )}

        {/* ── CLOSE SECTION ── */}
        <div
          id="close"
          className={highlightedStageId === "close" ? "scroll-mt-4 rounded-lg ring-2 ring-violet-300/80 transition-all" : "scroll-mt-4"}
        >
        <SvrStageSection
          label="Close"
          active={false}
          past={["closed", "waived"].includes(visitStatusKey)}
          open={expandedStageId === "close"}
          onRequestExpand={() => setExpandedStageId("close")}
        >
        {/* Waiver Request (for PIC/Sales) */}
        {visit.billable && isSales && !visit.waiverRequested && ["pending_review", "closed", "report_sent"].includes(visitStatusKey) && (
          <Card>
            <CardContent className="pt-4">
              {showWaiverForm ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium">Request Billing Waiver</p>
                  <Textarea
                    value={waiverReason}
                    onChange={(e) => setWaiverReason(e.target.value)}
                    placeholder="Reason for waiver request..."
                    rows={2}
                  />
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowWaiverForm(false)} className="flex-1">Cancel</Button>
                    <Button
                      onClick={() => requestWaiver.mutate({ id: visitId, reason: waiverReason })}
                      disabled={requestWaiver.isPending || !waiverReason.trim()}
                      className="flex-1 bg-black text-white hover:bg-gray-800"
                    >
                      Submit Waiver Request
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowWaiverForm(true)}
                  className="text-sm text-purple-700 hover:text-purple-900 flex items-center gap-1"
                >
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Request billing waiver
                </button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Customer Notification Panel — visible to PIC and admin */}
        <CustomerNoticePanel
          sourceType="svr"
          sourceId={visitId}
          canSend={isSales}
        />

        {/* Cleo Conversation Log */}
        <CleoConversationLog svrId={visitId} />

        {/* Closure info */}
        {visitStatusKey === "closed" && visit.closedAt && (
          <div className="text-xs text-muted-foreground text-center py-2">
            Closed by {visit.closedBy} on {formatDateTime(visit.closedAt)}
          </div>
        )}
        </SvrStageSection>{/* end CLOSE section */}
        </div>

        <section data-testid="svr-activity" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Activity</p>
          {activityEvents.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">No recorded history on this visit yet.</p>
          ) : (
            <ol className="mt-4 space-y-3">
              {activityEvents.map((event) => (
                <li key={event.id} className="flex gap-3 text-sm">
                  <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-slate-300" />
                  <span>
                    <span className="font-medium text-slate-900">{event.action}</span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      {[event.person, formatDateTime(event.at)].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>

        <p className="pt-2 text-center text-[11px] text-slate-400">
          All times are in Singapore Time (GMT+8)
          {visit.updatedAt && (
            <>
              {" "}
              · Last updated: {formatDateTime(visit.updatedAt as string | Date)}
            </>
          )}
        </p>

        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Cancel this service visit?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              The visit will remain available as a cancelled record. Your name, the cancellation time, method, and reason will appear in the SVR and cancellation notice.
            </p>
            <div className="space-y-2">
              <Label htmlFor="svr-cancellation-reason">Reason for cancellation</Label>
              <Textarea
                id="svr-cancellation-reason"
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Why is this visit being cancelled?"
                rows={4}
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setCancelDialogOpen(false)}>
                Keep visit
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={deleteVisit.isPending || cancellationReason.trim().length < 3}
                onClick={() => deleteVisit.mutate({ id: visitId, reason: cancellationReason.trim() })}
              >
                {deleteVisit.isPending ? "Cancelling…" : "Cancel visit"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={waiveConfirmOpen} onOpenChange={setWaiveConfirmOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Waive charge?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              This will waive the charge. Do you want to proceed?
            </p>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setWaiveConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={waiveSvrCharge.isPending}
                onClick={() => waiveSvrCharge.mutate({ visitId })}
              >
                {waiveSvrCharge.isPending ? "Waiving…" : "Waive charge"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <MarkPaidReceiptModeDialog
          open={confirmMarkPaidOpen}
          onOpenChange={setConfirmMarkPaidOpen}
          customerEmail={customer?.email}
          pending={markPaid.isPending}
          onSubmit={(payload) => markPaid.mutate({ visitId, ...payload })}
        />

        {showMatchCustomerModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl border border-slate-200">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <div>
                  <h3 className="text-sm font-semibold">Match Customer</h3>
                  <p className="text-xs text-muted-foreground">Resolve unmatched customer for {visit.visitNumber}</p>
                </div>
                <button
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setShowMatchCustomerModal(false)}
                >
                  Close
                </button>
              </div>

              <div className="px-4 pt-3">
                <div className="inline-flex rounded-md border bg-slate-50 p-0.5">
                  <button
                    className={cn("px-3 py-1.5 text-xs font-medium rounded", matchTab === "existing" ? "bg-white shadow" : "text-muted-foreground")}
                    onClick={() => setMatchTab("existing")}
                  >
                    Find Existing
                  </button>
                  <button
                    className={cn("px-3 py-1.5 text-xs font-medium rounded", matchTab === "new" ? "bg-white shadow" : "text-muted-foreground")}
                    onClick={() => setMatchTab("new")}
                  >
                    Create New
                  </button>
                </div>
              </div>

              {matchTab === "existing" ? (
                <div className="space-y-3 p-4">
                  <Input
                    value={matchQuery}
                    onChange={(e) => setMatchQuery(e.target.value)}
                    placeholder="Search by name or phone"
                  />
                  <div className="max-h-56 overflow-auto rounded-md border">
                    {!customerSearchResults || customerSearchResults.length === 0 ? (
                      <p className="p-3 text-xs text-muted-foreground">No customers found.</p>
                    ) : (
                      customerSearchResults.map((c: any) => (
                        <button
                          key={c.id}
                          onClick={() => setSelectedCustomerId(c.id)}
                          className={cn(
                            "w-full border-b px-3 py-2 text-left last:border-b-0 hover:bg-slate-50",
                            selectedCustomerId === c.id && "bg-violet-50 border-violet-200"
                          )}
                        >
                          <p className="text-sm font-medium">{c.name}</p>
                          <p className="text-xs text-muted-foreground">{c.contact || "No phone"} · {c.address || "No address"}</p>
                        </button>
                      ))
                    )}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowMatchCustomerModal(false)}>Cancel</Button>
                    <Button
                      disabled={!selectedCustomerId || linkCustomer.isPending}
                      onClick={() => {
                        if (!selectedCustomerId) return;
                        linkCustomer.mutate({ visitId, customerId: selectedCustomerId });
                      }}
                    >
                      {linkCustomer.isPending ? "Linking..." : "LINK CUSTOMER"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 p-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Label>Name</Label>
                      <Input value={newCustomerName} onChange={(e) => setNewCustomerName(e.target.value)} />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input value={newCustomerPhone} onChange={(e) => setNewCustomerPhone(e.target.value)} />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input value={newCustomerEmail} onChange={(e) => setNewCustomerEmail(e.target.value)} />
                    </div>
                    <div className="sm:col-span-2">
                      <AddressSearchInput
                        value={newCustomerAddressQuery}
                        onAddressSelect={(address, postalCode) => {
                          setNewCustomerAddress(address);
                          setNewCustomerAddressQuery(address);
                          setNewCustomerPostalCode(postalCode);
                        }}
                        placeholder="Search by address or postal code..."
                      />
                    </div>
                    {newCustomerPostalCode && (
                      <div className="sm:col-span-2 flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Postal Code</span>
                        <span className="ml-auto font-mono font-semibold text-foreground">{newCustomerPostalCode}</span>
                      </div>
                    )}
                    {newCustomerAddress && (
                      <div className="sm:col-span-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">
                          Is this a condo or apartment building?
                        </label>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => setNewCustomerIsCondo(true)}
                            className={cn(
                              "flex-1 px-3 py-2 text-sm border transition-colors",
                              newCustomerIsCondo
                                ? "border-black bg-black text-white"
                                : "border-gray-200 text-gray-700 hover:border-black"
                            )}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => setNewCustomerIsCondo(false)}
                            className={cn(
                              "flex-1 px-3 py-2 text-sm border transition-colors",
                              !newCustomerIsCondo && newCustomerAddress
                                ? "border-black bg-black text-white"
                                : "border-gray-200 text-gray-700 hover:border-black"
                            )}
                          >
                            No
                          </button>
                        </div>
                      </div>
                    )}
                    {newCustomerIsCondo && (
                      <div className="sm:col-span-2">
                        <Label>Unit / Floor</Label>
                        <Input
                          value={newCustomerUnitNumber}
                          onChange={(e) => setNewCustomerUnitNumber(e.target.value)}
                          placeholder="e.g. #03-12"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowMatchCustomerModal(false)}>Cancel</Button>
                    <Button
                      disabled={!newCustomerName.trim() || createAndLinkCustomer.isPending}
                      onClick={() =>
                        createAndLinkCustomer.mutate({
                          visitId,
                          name: newCustomerName.trim(),
                          contact: newCustomerPhone.trim() || undefined,
                          email: newCustomerEmail.trim() || "",
                          address: newCustomerAddress.trim() || undefined,
                          unitNumber: newCustomerUnitNumber.trim() || undefined,
                          postalCode: newCustomerPostalCode.trim() || undefined,
                        })
                      }
                    >
                      {createAndLinkCustomer.isPending ? "Creating..." : "CREATE & LINK CUSTOMER"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        </div> {/* end main content column */}

        </div> {/* end flex w-full (spine + content) */}
      </div> {/* end page bg wrapper */}
    </DashboardLayout>
  );
}
// ─── Staff email delivery status (Communications Log) ─────────────────────────
function SvrStaffDeliveryPanel({
  visitId,
  canRetry,
}: {
  visitId: number;
  canRetry: boolean;
}) {
  const utils = trpc.useUtils();
  const { data: logs = [], isLoading } = trpc.siteVisits.listStaffNotificationLogs.useQuery(
    { visitId },
    { enabled: visitId > 0 },
  );
  const retry = trpc.siteVisits.retryStaffNotification.useMutation({
    onSuccess: (result) => {
      if (result.summary.sent > 0) toast.success("Staff email resent");
      else if (result.warnings.length) toast.warning(result.warnings[0]);
      else toast.message("Retry completed");
      void utils.siteVisits.listStaffNotificationLogs.invalidate({ visitId });
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Staff email delivery
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-xs text-muted-foreground">Loading delivery log…</p>
        ) : logs.length === 0 ? (
          <p className="text-xs text-muted-foreground">No staff email delivery rows for this visit yet.</p>
        ) : (
          <div className="space-y-2">
            {logs.slice(0, 12).map((row) => (
              <div
                key={row.id}
                className="flex items-start justify-between gap-2 rounded border px-2 py-1.5 text-xs"
              >
                <div className="min-w-0 space-y-0.5">
                  <p className="font-medium truncate">{row.triggerLabel}</p>
                  <p className="text-muted-foreground truncate">
                    {row.recipientDisplay ?? "—"}
                    {row.event ? ` · ${String(row.event).replace(/_/g, " ")}` : ""}
                    {row.qualifyingRoles ? ` · ${row.qualifyingRoles}` : ""}
                  </p>
                  {row.inventoryMessage ? (
                    <p className="text-amber-800 bg-amber-50 rounded px-1.5 py-1 whitespace-pre-wrap">
                      {row.inventoryMessage}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span
                    className={
                      row.status === "failed"
                        ? "text-red-600 font-medium"
                        : row.status === "sent" || row.status === "delivered"
                          ? "text-emerald-700 font-medium"
                          : "text-muted-foreground"
                    }
                  >
                    {row.status}
                  </span>
                  {canRetry && (row.status === "failed" || row.status === "queued") ? (
                    <button
                      type="button"
                      className="text-blue-600 hover:underline"
                      disabled={retry.isPending}
                      onClick={() => retry.mutate({ deliveryLogId: row.id })}
                    >
                      Retry
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── AddAssigneeRow Component ───────────────────────────────────────────────────────────────────────────────────
type AssigneeEntry = { userId: number; userName: string; role: "technician" | "programmer" | "installer" };

function AddAssigneeRow({
  allUsers,
  existingIds,
  onAdd,
}: {
  allUsers: Array<{ id: number; name?: string | null; role: string }>;
  existingIds: number[];
  onAdd: (u: AssigneeEntry) => void;
}) {
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<"technician" | "programmer" | "installer">("technician");

  const eligible = allUsers.filter(
    (u) =>
      ["service", "programming", "installer", "admin"].includes(u.role) &&
      !existingIds.includes(u.id)
  );

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <select
        value={selectedUserId}
        onChange={(e) => setSelectedUserId(e.target.value)}
        className="flex-1 min-w-[120px] border border-input rounded-md px-2 py-1.5 text-xs bg-background focus:outline-none focus:ring-1 focus:ring-ring"
      >
        <option value="">— Select staff —</option>
        {eligible.map((u) => (
          <option key={u.id} value={u.id}>{u.name ?? `User #${u.id}`}</option>
        ))}
      </select>
      <select
        value={selectedRole}
        onChange={(e) => setSelectedRole(e.target.value as AssigneeEntry["role"])}
        className="border border-input rounded-md px-2 py-1.5 text-xs bg-background focus:outline-none focus:ring-1 focus:ring-ring"
      >
        <option value="technician">Technician</option>
        <option value="programmer">Programmer</option>
        <option value="installer">Installer</option>
      </select>
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs"
        disabled={!selectedUserId}
        onClick={() => {
          const u = eligible.find((x) => x.id === parseInt(selectedUserId));
          if (!u) return;
          onAdd({ userId: u.id, userName: u.name ?? `User #${u.id}`, role: selectedRole });
          setSelectedUserId("");
        }}
      >
        Add
      </Button>
    </div>
  );
}

function CleoConversationLog({ svrId }: { svrId: number }) {
  const { data: messages, isLoading } = trpc.cleo.getConversationLog.useQuery({ svrId });

  if (isLoading) return null;
  if (!messages || messages.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Cleo Conversation Log
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex gap-2 ${
                msg.direction === "inbound" ? "justify-start" : "justify-end"
              }`}
            >
              <div
                className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                  msg.direction === "inbound"
                    ? "bg-gray-100 text-gray-800"
                    : "bg-black text-white"
                }`}
              >
                <p className="whitespace-pre-wrap">{msg.body}</p>
                {msg.mediaUrl && (
                  <a
                    href={msg.mediaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs underline opacity-75 mt-1 block"
                  >
                    📎 Attachment
                  </a>
                )}
                <p className={`text-xs mt-1 opacity-60`}>
                  {new Date(msg.createdAt).toLocaleString("en-SG", {
                    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                  })}
                </p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
